// Madrid, "Summer night on Gran Vía".
// Back to front: a warm June sky (dithered indigo down to the sodium glow of the city), a honey moon, faint stars,
// drifting cirrus and a plane now and then; the far city at the end of Gran Vía (the stepped Telefónica tower,
// Edificio España and Torre de Madrid); the two streets that fork at the Metrópolis: Calle de Alcalá on the left with
// the church of San José and its slate-spired bell tower, and the sweep of Gran Vía on the right with rooftop statues,
// lanterns and turrets, a cinema and a bar sign with chasing bulbs, a far rooftop neon and a big neon sign for a
// churros bar on a steel frame (the billboard); the floodlit Metrópolis itself: the columned rotunda, the black slate
// dome with its gold ribs and the winged Victory on top; the plaza below with a terrace café, ornate lamp posts,
// traffic lights, streams of headlights, night buses, white taxis with the red stripe, a street sweeper and an
// ambulance, and two bats hunting round the lamps; and our wrought-iron balcony: geraniums that lose a petal to each
// gust, a black cat that does a round of the tiles, a glass of tinto de verano on the rail, a folding chair with a fan,
// string lights and a wall lantern with moths round them, and the open shutter and net curtain of a warm room.
// Poplar fluff (pelusas) drifts through the warm air at three depths.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex, mix,
  makeCanvas, px, rect, makeHalo, drawHalo, text, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                              // this scene's 480x270 buffer, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);
const HXC = {};
const HX = h => HXC[h] || (HXC[h] = hex(h));          // cached #rrggbb -> [r, g, b]

/* ================= Scene constants ================= */
const HZ = 124;                                       // eye level: both streets vanish on this row
const VPL = 90, VPR = 390;                            // vanishing points of Calle de Alcalá (left) and Gran Vía (right)
const GR = 62, CR = 14;                               // at depth z the pavement is at HZ + GR / z, a 30 m cornice at HZ - CR / z
const gy = z => HZ + GR / z;                          // pavement row at depth z
const fy = (z, f) => HZ + (GR - f * (GR + CR)) / z;   // row at height f (0 pavement, 1 the Metrópolis cornice) at depth z
const MX = 200, MR = 19;                              // the Metrópolis rotunda: axis (focusX) and radius
const MBASE = 186, MCORN = 110;                       // its foot on the pavement and its main cornice (gy(1), fy(1, 1))
const RAIL = 206, FLOOR = 245;                        // the balcony handrail (top row) and floor
const JAMB = 436;                                     // the balcony door's jamb; the lit room is to its right
const SHUT = 424;                                     // the open shutter, folded against the wall left of the jamb
const MOON = { x: 404, y: 41, r: 13 };
// the streets' walls: x = vp + off / z
const AL_N = { vp: VPL, off: 91 };                    // Alcalá, north side: the Metrópolis wing, San José, …
const AL_S = { vp: VPL, off: -70 };                   // Alcalá, south side (our side of the street, far left)
const GV_S = { vp: VPR, off: -171 };                  // Gran Vía, south side: the Metrópolis wing and the sweep of facades
const GV_N = { vp: VPR, off: 40 };                    // Gran Vía, north side (far right)
const sideX = (s, z) => s.vp + s.off / z;
const sideZ = (s, x) => s.off / (x - s.vp);
// the cross street below the balcony runs the whole width in front of the plaza: its far curb is the plaza's front edge,
// and the corner buildings of Alcalá (left) and Gran Vía (right) front onto it behind a strip of pavement
const CURB = 0.87, CORNER = 0.95;

/* ================= Sky ================= */
const SKY_P = ['#08091f', '#0b0b25', '#0e0d2b', '#121031', '#161237', '#1b153d', '#211842', '#281b47', '#301e4b',
  '#39224e', '#432651', '#4f2a53', '#5c2f54', '#6a3554', '#783c53', '#864450', '#944e4d', '#a25a4a'].map(hex);
const SKY_M = ['#12143a', '#15163f', '#191844', '#1d1b49', '#221e4e', '#282152', '#2e2456', '#352859', '#3d2c5b',
  '#462f5d', '#50335e', '#5a375e', '#663c5e', '#72425d', '#7e485b', '#8a5058', '#965954', '#a26250'].map(hex);
function renderSky() {
  const [c, x] = makeCanvas(W, H);
  const img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / (HZ + 4)), 1.55);
    const glow = Math.max(0, 1 - Math.hypot((i - VPR + 20) / 230, (y - HZ) / 75)) * 0.3
      + Math.max(0, 1 - Math.hypot((i - MX) / 170, (y - HZ) / 90)) * 0.2 + Math.max(0, 1 - Math.hypot((i - VPL) / 140, (y - HZ) / 60)) * 0.15;
    v = Math.min(1, v + glow * (0.3 + v));
    const f = v * (SKY_P.length - 1), b = bayer(i, y);
    let k = Math.floor(f);
    if (f - k > b) k++;
    k = Math.min(k, SKY_P.length - 1);
    const moon = Math.max(0, 1 - Math.hypot((i - MOON.x) / 130, (y - MOON.y) / 95)) * 1.1;
    const col = moon > bayer(i + 1, y + 2) ? SKY_M[k] : SKY_P[k], o = (y * W + i) * 4;
    d[o] = col[0]; d[o + 1] = col[1]; d[o + 2] = col[2]; d[o + 3] = 255;
  }
  x.putImageData(img, 0, 0);
  return c;
}
const STARS = [];
{
  const r = rng(61);
  while (STARS.length < 72) {
    const x = Math.floor(r() * W), y = Math.floor(Math.pow(r(), 1.35) * 100);
    if (Math.hypot(x - MOON.x, y - MOON.y) < 27 || (Math.abs(x - MX) < 13 && y > 36)) continue;
    const c = r();
    STARS.push({ x, y, k: 11 + Math.floor(r() * 70), ph: r(), b: (0.3 + r() * 0.6) * (1 - y / 150), big: r() < 0.05,
      col: c < 0.7 ? '#e8e8ff' : c < 0.88 ? '#fff0d0' : '#d0e0ff' });
  }
}
function drawStars(t) {
  for (const s of STARS) {
    const a = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph));
    if (s.big) { ctx.globalAlpha = a * 0.3; rect(ctx, s.x - 1, s.y, 3, 1, s.col); rect(ctx, s.x, s.y - 1, 1, 3, s.col); }
    ctx.globalAlpha = a; px(ctx, s.x, s.y, s.col);
  }
  ctx.globalAlpha = 1;
}
function renderMoon() {                               // a honey-coloured June moon, low and warm
  const r = MOON.r, s = r * 2 + 1, [c, x] = makeCanvas(s, s);
  const maria = [[-4, -3, 3.2], [3, -5, 2.2], [4, 2, 2.6], [-2, 5, 2.0], [-7, 2, 1.6], [7, -1, 1.3], [0, 0, 1.2]];
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const dx = i - r, dy = y - r, dd = Math.hypot(dx, dy);
    if (dd > r + 0.35) continue;
    let col = dx + dy < -9 ? '#fff6dc' : dx + dy < 5 ? '#fbe8bf' : '#f0d8a8';
    for (const [cx, cy, cr] of maria) if (Math.hypot(dx - cx, dy - cy) < cr) col = dx + dy < 0 ? '#ead2a2' : '#dcc090';
    if (dd > r - 1.1) col = dx + dy > 3 ? '#d8bc8a' : '#f6e2b6';
    px(x, i, y, col);
  }
  return c;
}

const SHOOTS = [{ t: 96.3, x: 262, y: 10, dx: -64, dy: 22 }, { t: 211.7, x: 300, y: 18, dx: 60, dy: 24 }];
function drawShootingStars(t) {
  for (const s of SHOOTS) {
    const u = (t - s.t) / 1.1;
    if (u < 0 || u > 1) continue;
    const a = Math.sin(Math.PI * u);
    for (let k = 0; k < 16; k++) {
      const uu = u - k * 0.02;
      if (uu < 0) break;
      ctx.globalAlpha = a * (1 - k / 16);
      px(ctx, Math.round(s.x + s.dx * uu), Math.round(s.y + s.dy * uu), k < 2 ? '#ffffff' : k < 7 ? '#fff0dc' : '#d8b8e0');
    }
    halo(HL.strobe, s.x + s.dx * u, s.y + s.dy * u, a * 0.6);
  }
  ctx.globalAlpha = 1;
}

/* ================= Cirrus wisps (lit from below by the city) ================= */
const WISPS = [
  { y: 22, w: 170, h: 10, k: 1, off: 60, a: 0.26, seed: 3 }, { y: 58, w: 150, h: 9, k: 1, off: 300, a: 0.34, seed: 1 },
  { y: 86, w: 120, h: 7, k: 2, off: 170, a: 0.3, seed: 2 },
];
function renderWisp(wd) {
  const [c, x] = makeCanvas(wd.w, wd.h);
  for (let j = 0; j < wd.h; j++) for (let i = 0; i < wd.w; i++) {
    let e = 1 - Math.hypot((i - wd.w / 2) / (wd.w / 2), (j - wd.h / 2) / (wd.h / 2));
    e += 0.3 * Math.sin(i * 0.085 + j * 0.9 + wd.seed * 2) + 0.18 * Math.sin(i * 0.23 - j * 0.5 + wd.seed);
    if (e * 1.3 < bayer(i, j) + hash(i >> 2, j, wd.seed) * 0.45) continue;
    px(x, i, j, j > wd.h * 0.6 ? '#8a5a6a' : j > wd.h * 0.3 ? '#6a4a6c' : '#4e3e68');
  }
  return c;
}
function drawWisps(t) {
  WISPS.forEach((wd, i) => {
    const span = W + wd.w, sx = (scroll(t, wd.k, span) + wd.off) % span - wd.w;
    ctx.globalAlpha = wd.a;
    ctx.drawImage(WISP_C[i], sx, wd.y);
  });
  ctx.globalAlpha = 1;
}

/* ================= Planes on the way to Barajas ================= */
const PLANES = [{ a: 12, b: 72, x0: -8, x1: 488, y0: 32, y1: 17 }, { a: 150, b: 198, x0: 488, x1: -8, y0: 13, y1: 27 }];
function drawPlanes(t) {
  for (const p of PLANES) {
    if (t < p.a || t >= p.b) continue;
    const u = (t - p.a) / (p.b - p.a), x = Math.round(lerp(p.x0, p.x1, u)), y = Math.round(lerp(p.y0, p.y1, u));
    const dir = p.x1 > p.x0 ? 1 : -1;
    rect(ctx, x - 2, y, 5, 1, '#1c1a2c');
    px(ctx, x + 2 * dir, y, '#fff6dc'); halo(HL.landing, x + 2 * dir, y, 0.6);                     // landing light
    if (frac(t * 0.9) < 0.14) { px(ctx, x, y + 1, '#ff4040'); halo(HL.red, x, y + 1, 0.7); }       // beacon
    const f = frac(t / 1.4);
    if (f < 0.05 || (f > 0.12 && f < 0.17)) { px(ctx, x - 2 * dir, y, '#ffffff'); halo(HL.strobe, x - 2 * dir, y, 0.7); }
  }
}

/* ================= Pixel painter for the big static canvases ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x,
    put(i, y, col) {
      if (i < 0 || y < 0 || i >= w || y >= h) return;
      const o = (y * w + i) * 4, v = HX(col);
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = 255;
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
const pick = (pal, v, i, y) => {                      // ordered-dither v (0..1) across a list of colours
  const f = clamp(v, 0, 1) * (pal.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, y)) k++;
  return pal[Math.min(k, pal.length - 1)];
};

/* ================= The far city at the end of Gran Vía ================= */
function renderFar() {
  const P = painter(W, HZ + 2);
  for (let i = 0; i < W; i++) {                       // distant roofs peeking over the far ends of both streets
    const n = Math.sin(i * 0.21) * 1.5 + Math.sin(i * 0.057 + 1) * 2.5 + (hash(i >> 3, 0, 5) - 0.5) * 5;
    let top = Math.round(HZ - 8 - n);
    if (hash(i >> 2, 1, 5) < 0.08) top -= 3;
    for (let y = top; y <= HZ + 1; y++)
      P.put(i, y, y === top ? '#3c2e50' : hash(i, y, 6) < 0.05 && y > top + 1 ? (hash(i, y, 7) < 0.5 ? '#b08058' : '#8a6a58') : '#271e3c');
  }
  // stepped tiers, widest (lowest) first: each is lit on its left edge and capped with a lit ledge, and runs down over
  // the ledge of the tier below; windows keep one floor grid; the warm haze over the city thickens towards the roofs.
  // o.crease splits a face into two planes (the right one in M.alt), o.ribs marks vertical piers, o.wtop keeps windows
  // below that row
  const tower = (tiers, M, o = {}) => {
    const { crease = -1, ribs = false, wy = 3, p = 0.2, seed = 0, mist = 0.55, wtop = 0 } = o;
    tiers.forEach(([a, b, top], k) => {
      const bottom = k ? tiers[k - 1][2] : HZ + 1;
      for (let y = top; y <= bottom; y++) for (let i = a; i <= b; i++) {
        const inner = i > a && i < b && i !== crease;
        let col = i === a ? M.lit : i === b || i === crease ? M.dark : crease > 0 && i > crease ? M.alt
          : ribs && !((i - a) & 1) ? M.pier : M.mid;
        if (y === top) col = M.cap;
        else if (inner && ((i - a) & 1) && y % wy === 0 && y >= wtop && hash(i, y, seed) < p) col = M.win;
        if (y > top && bayer(i, y) < clamp((y - 98) / 36, 0, mist)) col = M.haze;
        P.put(i, y, col);
      }
    });
  };
  const FAR = { cap: '#4c3e5e', lit: '#443858', mid: '#31294a', alt: '#29223f', dark: '#1f1a34', pier: '#2b2444',
    win: '#a08064', haze: '#4a3556' };
  const NEAR = { cap: '#5e4a66', lit: '#54425f', mid: '#3a2e4e', alt: '#302642', dark: '#221a32', pier: '#32283f',
    win: '#e0a868', haze: '#4e3858' };
  // Torre de Madrid, the tallest: a slender slab whose front folds into two planes, a broad cornice, a penthouse and
  // a thin mast; it stands behind Edificio España at the far end of Gran Vía
  tower([[362, 372, 64]], FAR, { crease: 367, wy: 2, p: 0.12, seed: 32 });
  P.put(361, 64, FAR.cap); P.put(373, 64, FAR.cap);                             // the cornice overhangs
  tower([[365, 369, 62]], FAR, { p: 0 });                                        // penthouse
  for (let y = 54; y < 62; y++) P.put(367, y, '#3a3052');                        // mast
  // Edificio España: much wider, stepped up in four heights to a central tower crowned with pinnacles
  tower([[336, 364, 101], [339, 361, 92], [345, 355, 80], [347, 353, 76], [349, 351, 74]], FAR, { p: 0.18, seed: 31 });
  for (const x of [347, 353]) { P.put(x, 75, FAR.lit); P.put(x, 74, FAR.cap); }  // pinnacles
  P.put(350, 73, '#3a3052');
  // the Telefónica building where Gran Vía bends: nearer, a narrower shaft of piers that steps in like a wedding cake
  // to a lantern and a short mast, the round clock glowing blue on its tower
  tower([[382, 392, 90], [384, 390, 85], [385, 389, 81], [386, 388, 78]], NEAR,
    { ribs: true, p: 0.25, seed: 33, mist: 0.35, wtop: 88 });
  P.put(387, 83, '#7a90d8');
  for (let y = 74; y < 78; y++) P.put(387, y, '#3e3252');
  return P.done();
}
const BEACONS = [{ x: 367, y: 53, ph: 0 }, { x: 350, y: 72, ph: 0.4 }, { x: 387, y: 73, ph: 0.7 }];

/* ================= The streets' walls ================= */
// facade materials at night, [upper (dark), middle, lower (lamp-lit), cornice highlight]
const PALS = [
  ['#3e3448', '#5a4a58', '#7c6660', '#a88c72'],       // cream stone
  ['#3e2e44', '#5a3e4e', '#7a5258', '#a07068'],       // pink stone
  ['#3e3240', '#5c4644', '#80624a', '#aa865e'],       // ochre
  ['#34263a', '#4e3040', '#6c3e40', '#946050'],       // red brick
];
const WIN_LIT = ['#ffcf7a', '#ffb65c', '#ffe2a8', '#ffc46a', '#ffd890', '#8ab4ff'];
const PZ = 9 / 171;                                   // window spacing along a street, in depth units
// a building on a receding wall: z0..z1 along the street, f its height (1 = the Metrópolis cornice), roof a mansard
// above it, pal its material, lit the share of lit windows, shop the colour of its lit ground floor, fl its floors;
// pane: even panes, 1 px wide and this many rows tall, one per window column and floor (for a front whose window
// spacing shrinks to 2-3 px, where the free-running grid would alias into a mix of 1 and 2 px wide panes)
const GV_ROW = [
  { z0: 1.18, z1: 1.6, f: 1.02, pal: 0, lit: 0.45, shop: '#fff0d4', fl: 6 },
  { z0: 1.6, z1: 2.1, f: 1.1, pal: 1, lit: 0.35, shop: '#ffc070', fl: 6, roof: 0.08, pane: 3 },
  { z0: 2.1, z1: 2.7, f: 1.2, pal: 2, lit: 0.4, shop: '#ffd890', fl: 7 },
  { z0: 2.7, z1: 3.4, f: 1.0, pal: 3, lit: 0.35, shop: '#fff0d0', fl: 6, roof: 0.1 },
  { z0: 3.4, z1: 4.4, f: 1.15, pal: 0, lit: 0.4, shop: '#ffc878', fl: 7 },
  { z0: 4.4, z1: 6, f: 1.05, pal: 2, lit: 0.35, shop: '#ffe0a0', fl: 6, roof: 0.1 },
  { z0: 6, z1: 9, f: 1.22, pal: 1, lit: 0.4, shop: '#ffd080', fl: 7 },
  { z0: 9, z1: 30, f: 1.1, pal: 3, lit: 0.3, shop: '#ffc070', fl: 6 },
];
const AL_ROW = [
  { z0: 1.8, z1: 2.4, f: 1.08, pal: 2, lit: 0.4, shop: '#ffd890', fl: 6, roof: 0.1 },
  { z0: 2.4, z1: 3.2, f: 1.2, pal: 0, lit: 0.35, shop: '#ffe6b8', fl: 7 },
  { z0: 3.2, z1: 4.5, f: 1.0, pal: 3, lit: 0.4, shop: '#ffc070', fl: 6, roof: 0.12 },
  { z0: 4.5, z1: 7, f: 1.15, pal: 1, lit: 0.35, shop: '#ffd890', fl: 7 },
  { z0: 7, z1: 30, f: 1.1, pal: 2, lit: 0.3, shop: '#ffc878', fl: 6 },
];
const ALS_ROW = [
  { z0: CORNER, z1: 1.0, f: 1.22, pal: 1, lit: 0.45, shop: '#ffe0a8', fl: 7, roof: 0.1, front: true },
  { z0: 1.0, z1: 1.35, f: 1.1, pal: 0, lit: 0.4, shop: '#d8ffe4', fl: 6 },
  { z0: 1.35, z1: 1.9, f: 1.28, pal: 2, lit: 0.4, shop: '#ffd08a', fl: 7 },
  { z0: 1.9, z1: 2.8, f: 1.05, pal: 3, lit: 0.35, shop: '#ffc070', fl: 6, roof: 0.1 },
  { z0: 2.8, z1: 5, f: 1.18, pal: 0, lit: 0.35, shop: '#ffe0a0', fl: 7 },
  { z0: 5, z1: 30, f: 1.1, pal: 1, lit: 0.3, shop: '#ffc878', fl: 6 },
];
const GVN_ROW = [
  { z0: CORNER, z1: 1.25, f: 1.15, pal: 2, lit: 0.45, shop: '#ffe6b8', fl: 7, roof: 0.1, front: true },
  { z0: 1.25, z1: 1.9, f: 1.05, pal: 0, lit: 0.4, shop: '#ffd890', fl: 6 },
  { z0: 1.9, z1: 3, f: 1.2, pal: 1, lit: 0.4, shop: '#fff0d0', fl: 7 },
  { z0: 3, z1: 30, f: 1.1, pal: 3, lit: 0.3, shop: '#ffc070', fl: 6 },
];
const WIN_TOG = [];                                   // windows that switch on and off: { id, spans: [[x, y0, y1]], col, n, off }
function paintWall(P, side, b, seed) {
  const xa = sideX(side, b.z0), xb = sideX(side, b.z1), pal = PALS[b.pal], roof = b.roof || 0, fh = (b.f - 0.27) / b.fl;
  const tog = new Map();
  const glass = (wc, fi, i, y, sky, sill) => {        // window wc on floor fi: lit (some switch on and off) or dark glass
    if (hash(wc, fi, seed * 7 + 1) >= b.lit) return sky ? '#2a2a3e' : '#1a1422';
    const lc = WIN_LIT[Math.floor(hash(wc, fi, seed * 7 + 2) * WIN_LIT.length)];
    if (hash(wc, fi, seed * 7 + 3) < 0.28) {          // this one switches on and off
      const key = wc * 64 + fi;
      if (!tog.has(key)) tog.set(key, { spans: [], col: lc });
      const w = tog.get(key), last = w.spans[w.spans.length - 1];
      if (last && last[0] === i) last[2] = y; else w.spans.push([i, y, y]);
      return '#1c1624';
    }
    return sill ? '#1c1624' : lc;
  };
  // one column of the building at depth z: q and q2 step once per window and per shopfront, period is the window
  // spacing on screen here, edge paints it as a lit pilaster; panes (pane mode): row -> [floor, top row?] of window pc
  const column = (i, z, q, q2, period, edge, panes, pc) => {
    const unit = (GR + CR) / z, bot = gy(z), top = Math.round(fy(z, b.f)), rtop = Math.round(fy(z, b.f + roof));
    const col = Math.floor(q), qf = q - col, qf2 = q2 - Math.floor(q2);
    for (let y = rtop; y <= Math.round(bot); y++) {
      const h = (bot - y) / unit;
      let c;
      if (y < top) {                                  // mansard: slate with lit dormers
        c = y === rtop ? '#4a4258' : (period > 2.2 && qf > 0.4 && qf < 0.7 && y > rtop + 1 && y < top - 1)
          ? (hash(col, 99, seed) < 0.35 ? '#e8b070' : '#1a1622') : bayer(i, y) < 0.3 ? '#2a2434' : '#221c2c';
      } else if (y === top) c = pal[3];
      else if (y === top + 1) c = '#241a2a';
      else if (h < 0.15) {                            // ground floor: shopfronts
        if (h > 0.125) c = '#261a26';
        else if (h > 0.02 && qf2 > 0.12 && qf2 < 0.86) c = h > 0.1 ? '#c89a6a' : b.shop;
        else c = '#1c1420';
      } else {
        const L = clamp(1 - (h - 0.15) / (b.f - 0.1), 0, 1);
        c = pick(pal.slice(0, 3), 0.15 + L * 0.8, i, y);
        if (edge) c = pal[2];
        const fi = Math.floor((h - 0.17) / fh), fp = (h - 0.17) / fh - fi;
        if (panes) {
          const pw = panes.get(y);
          if (pw) c = glass(pc, pw[0], i, y, pw[1], false);
        } else if (fi >= 0 && fi < b.fl) {
          if (period >= 2.2) {
            if (qf > 0.34 && qf < 0.8 && fp > 0.22 && fp < 0.78) c = glass(col, fi, i, y, fp > 0.7, fp > 0.68);
            else if (period >= 3 && qf > 0.28 && qf < 0.86 && fp > 0.12 && fp <= 0.22) c = '#140e18';   // iron balcony
          } else if (fp > 0.3 && fp < 0.62) {
            const hv = hash(i, fi, seed * 3);
            if (hv < b.lit * 0.5) c = WIN_LIT[Math.floor(hash(i, fi, seed * 3 + 1) * 5)];
            else if (hv < 0.6) c = '#1e1826';
          }
        }
      }
      P.put(i, y, c);
    }
  };
  for (let i = Math.ceil(Math.min(xa, xb)); i <= Math.floor(Math.max(xa, xb)); i++) {
    if (i < 0 || i >= W) continue;
    const z = sideZ(side, i + 0.5);
    if (z < b.z0 || z > b.z1) continue;
    let panes = null, pc = -1;
    if (b.pane) {
      const qa = sideZ(side, i) / PZ + seed, qb = sideZ(side, i + 1) / PZ + seed, n = Math.floor(Math.max(qa, qb) - 0.57);
      const zc = (n + 0.57 - seed) * PZ;              // the window's centre, which must be on this building
      panes = new Map();
      if (n + 0.57 >= Math.min(qa, qb) && zc >= b.z0 && zc <= b.z1) {
        pc = n;
        for (let fi = 0; fi < b.fl; fi++) {
          const y0 = Math.round(gy(z) - (0.17 + (fi + 0.5) * fh) * ((GR + CR) / z)) - (b.pane >> 1);
          for (let r = 0; r < b.pane; r++) panes.set(y0 + r, [fi, r === 0]);
        }
      }
    }
    column(i, z, z / PZ + seed, z / (PZ * 1.6) + seed * 0.7, PZ * Math.abs(side.off) / (z * z),   // window spacing
      Math.abs(z - b.z0) * Math.abs(side.off) / (z * z) < 1, panes, pc);                       // near edge: a pilaster
  }
  // front: a corner building's face on the cross street, square to us at z0, d units from the corner (a window every 9)
  if (b.front) for (let i = 0; i < W; i++) {
    const d = Math.sign(side.off) * ((i + 0.5 - side.vp) * b.z0 - side.off);
    if (d > 0) column(i, b.z0, d / 9 + 0.1, d / 14.4 + 0.05, 9 / b.z0, false, null, -1);
  }
  for (const w of tog.values()) WIN_TOG.push({ id: WIN_TOG.length, spans: w.spans, col: w.col, n: 2 + Math.floor(hash(WIN_TOG.length, 1, 71) * 6), p: 0.35 + hash(WIN_TOG.length, 2, 71) * 0.4, off: hash(WIN_TOG.length, 3, 71) });
}

/* ================= San José: a baroque front of brick and stone, the tower with its slate chapitel ================= */
const SJ = { z0: 1.35, z1: 1.8 };
const TOWER = { x: 150, w: 8, top: 96 };              // tower body (frontal) above the church roof
const BELL = { x: 153, y: 89 };                       // the bell in the belfry opening
const SPIRE = [                                       // chapitel rows from y = 84 upward: [first col, last col]
  [0, 7], [0, 7], [1, 6], [1, 6], [0, 7], [0, 7], [1, 6], [1, 6], [2, 5], [2, 5], [3, 4], [3, 4], [3, 4],
];
function paintChurch(P) {
  for (let i = 138; i <= 158; i++) {
    const z = sideZ(AL_N, i + 0.5);
    if (z < SJ.z0 || z > SJ.z1) continue;
    const unit = (GR + CR) / z, bot = gy(z), u = (z - SJ.z0) / (SJ.z1 - SJ.z0);
    const fTop = 0.9 + 0.12 * Math.max(0, 1 - Math.abs(u - 0.55) / 0.22);
    const top = Math.round(fy(z, fTop));
    for (let y = top; y <= Math.round(bot); y++) {
      const h = (bot - y) / unit, lit = clamp(1 - h * 0.7, 0.25, 1);
      const stone = (u > 0.26 && u < 0.34) || (u > 0.76 && u < 0.84) || u < 0.06 || u > 0.95 || Math.abs(h - 0.42) < 0.03;
      let c = stone ? pick(['#7a5a4a', '#b08460', '#e0b480'], lit, i, y) : pick(['#5a3028', '#7a4434', '#9e5c44'], lit, i, y);
      if (y === top) c = '#f0c890';
      else if (y === top + 1) c = '#6a4436';
      if (u > 0.46 && u < 0.64 && h < 0.3) c = h > 0.26 ? '#e8bc88' : h > 0.22 ? '#3a2018' : pick(['#4a2a1c', '#8a5a34', '#c88a4a'], 0.9 - h * 2.5, i, y);   // portal
      if (u > 0.5 && u < 0.6 && h > 0.48 && h < 0.62) c = h > 0.53 && h < 0.59 && u > 0.53 && u < 0.57 ? '#f0d8b0' : '#3a2420';          // niche
      P.put(i, y, c);
    }
  }
  const { x: tx, w: tw, top } = TOWER, tb = Math.round(fy(sideZ(AL_N, tx + 4), 0.9)) + 1;
  for (let y = top; y <= tb; y++) for (let k = 0; k < tw; k++) {                // brick body with stone quoins
    const lit = clamp((y - top) / 30, 0, 1);
    let c = k === 0 || k === tw - 1 ? ((y >> 1) & 1 ? pick(['#8a6a54', '#d4a878'], lit + 0.3, tx + k, y) : '#6a4a3c')
      : pick(['#4a2a26', '#6e3c30', '#8a4c3a'], lit, tx + k, y);
    if (k >= 3 && k <= 4 && y >= top + 7 && y <= top + 10) c = '#1c1216';
    P.put(tx + k, y, c);
  }
  for (let k = -1; k <= tw; k++) { P.put(tx + k, top - 1, '#e8bc8c'); P.put(tx + k, 86, '#f0c894'); P.put(tx + k, 85, '#9a7058'); }
  for (let y = 87; y < top - 1; y++) for (let k = 0; k < tw; k++) {              // belfry: stone frame, warm-lit opening
    const open = k >= 2 && k <= 5 && y >= 88 && !(y === 88 && (k === 2 || k === 5));
    P.put(tx + k, y, open ? (y < 90 ? '#7a4a2e' : '#a0643a') : k === 0 ? '#e8c090' : k === tw - 1 ? '#8a6448' : '#c8986a');
  }
  SPIRE.forEach(([a, b], j) => {
    const y = 84 - j;
    for (let k = a; k <= b; k++) P.put(tx + k, y, k === a ? '#4c4864' : k === b ? '#14121c' : j < 4 ? '#3a3246' : (y & 1) ? '#2a2838' : '#232130');
  });
  P.put(tx + 3, 71, '#f0c060'); P.put(tx + 4, 71, '#b0802e'); P.put(tx + 3, 70, '#ffe08a'); P.put(tx + 4, 70, '#d09a40');   // gilded ball
  for (let y = 66; y < 70; y++) P.put(tx + 4, y, '#2e2838');
  P.put(tx + 3, 67, '#2e2838'); P.put(tx + 5, 67, '#2e2838');                  // iron cross
}

/* ================= Rooftop life along Gran Vía (static parts) ================= */
const TURRET = { x: 308, z: 2.15 };                   // a corner turret with a slate dome and a lit lantern
const ROOF_LAMPS = [], ROOF_STATUES = [];
{
  ROOF_LAMPS.push({ x: Math.round(sideX(AL_N, 2.0)), y: Math.round(fy(2.0, 1.18)) - 3 });
  ROOF_LAMPS.push({ x: Math.round(sideX(AL_N, 2.3)), y: Math.round(fy(2.3, 1.18)) - 3 });
  for (const [z, f] of [[2.4, 1.2], [2.62, 1.2], [3.8, 1.15], [4.15, 1.15], [2.02, 1.18]]) ROOF_STATUES.push({ x: Math.round(sideX(GV_S, z)), y: Math.round(fy(z, f)) });
  for (const [z, f] of [[2.6, 1.2], [3.0, 1.2]]) ROOF_STATUES.push({ x: Math.round(sideX(AL_N, z)), y: Math.round(fy(z, f)) });
}
// blade signs hanging off the facades (they face us, the street runs away from us); text runs down, bulbs chase round
const BLADES = [
  { word: 'CINE', x: 291, y: 124, col: '#ff5a6e' },
  { word: 'BAR', x: 318, y: 123, col: '#7ae8ff' },
];
const BLADE_BULBS = BLADES.map(b => {
  const h = b.word.length * 6 + 1, out = [];
  for (let y = b.y; y < b.y + h; y += 2) out.push([b.x - 1, y]);
  for (let x = b.x + 1; x < b.x + 4; x += 2) out.push([x, b.y + h]);
  for (let y = b.y + h; y > b.y; y -= 2) out.push([b.x + 5, y]);
  for (let x = b.x + 3; x > b.x; x -= 2) out.push([x, b.y - 1]);
  return out;
});
// the theatre's marquee: bulbs along the top of the second building's ground floor
const MARQUEE = [];
for (let i = Math.ceil(sideX(GV_S, 1.62)); i <= Math.floor(sideX(GV_S, 2.08)); i += 2) MARQUEE.push([i, Math.round(fy(sideZ(GV_S, i + 0.5), 0.16))]);
const HOSTAL = { x: 327, y: 109 };                    // a small rooftop neon far along the street

function renderCity() {                               // every wall, San José and the rooftop furniture, in one canvas
  const P = painter(W, H);
  ALS_ROW.forEach((b, k) => paintWall(P, AL_S, b, 0.13 + k * 0.29));
  AL_ROW.forEach((b, k) => paintWall(P, AL_N, b, 0.41 + k * 0.37));
  GV_ROW.forEach((b, k) => paintWall(P, GV_S, b, 0.27 + k * 0.31));
  GVN_ROW.forEach((b, k) => paintWall(P, GV_N, b, 0.61 + k * 0.23));
  paintChurch(P);
  const c = P.done(), x = P.x;
  // turret: a round drum, slate dome, lit lantern and a needle
  { const { x: t } = TURRET, base = Math.round(fy(TURRET.z, 1.2));
    rect(x, t - 2, base - 6, 5, 6, '#6a5458'); rect(x, t - 2, base - 6, 1, 6, '#a8886e'); rect(x, t + 2, base - 6, 1, 6, '#3a2c38');
    px(x, t, base - 4, '#ffd08a'); px(x, t - 1, base - 3, '#1c1622'); px(x, t + 1, base - 3, '#1c1622');
    rect(x, t - 3, base - 7, 7, 1, '#b8987a');
    rect(x, t - 2, base - 10, 5, 3, '#2a2638'); rect(x, t - 1, base - 11, 3, 1, '#2a2638'); px(x, t - 2, base - 10, '#4e4a64');
    px(x, t - 1, base - 9, '#4e4a64'); px(x, t, base - 11, '#4e4a64');
    rect(x, t - 1, base - 14, 3, 3, '#3a2e30'); px(x, t, base - 13, '#ffe0a0');
    rect(x, t, base - 17, 1, 3, '#2e2838'); }
  for (const s of ROOF_STATUES) {                     // statues on the cornices, lit from below
    rect(x, s.x, s.y - 4, 2, 4, '#241c2a'); px(x, s.x, s.y - 5, '#241c2a');
    px(x, s.x, s.y - 1, '#8a6a58'); px(x, s.x + 1, s.y - 2, '#5a4448'); px(x, s.x - 1, s.y - 3, '#241c2a');
  }
  for (const l of ROOF_LAMPS) { rect(x, l.x, l.y + 1, 1, 3, '#2a2230'); px(x, l.x, l.y, '#ffe6b0'); }
  for (const l of LAMPS) {                            // street lamps down both streets
    rect(x, l.x, l.y + 1, 1, Math.round(gy(l.z)) - l.y - 1, l.z < 1.6 ? '#2a2230' : '#261e2e');
    if (l.z < 1.6) { rect(x, l.x - 1, l.y, 3, 2, '#ffe6b0'); px(x, l.x, l.y - 1, '#2a2230'); } else px(x, l.x, l.y, '#ffe6b0');
  }
  // blade sign boxes (the bulbs and letters are lit per frame)
  for (const b of BLADES) {
    const h = b.word.length * 6 + 1;
    rect(x, b.x, b.y, 5, h, '#140c16'); rect(x, b.x, b.y, 5, 1, '#3a2a36');
  }
  // the far rooftop neon's frame
  rect(x, HOSTAL.x - 1, HOSTAL.y - 1, 25, 7, '#120c18');
  for (const lx of [HOSTAL.x + 2, HOSTAL.x + 11, HOSTAL.x + 20]) rect(x, lx, HOSTAL.y + 6, 1, 3, '#1c1624');
  // the pharmacy cross on the left, sticking out of its wall
  rect(x, PHARM.x - 3, PHARM.y - 3, 7, 7, '#101a14'); rect(x, PHARM.x + 4, PHARM.y - 1, 2, 1, '#2a2430');
  return c;
}
const PHARM = { x: 30, y: 150 };                      // the green cross of a chemist's, blinking
const SIGN_BUZZ = [[37.2, 38.6], [133.5, 134.4], [201.1, 202.7]];
function drawCityFx(t) {
  for (const w of WIN_TOG) {                          // windows switching on and off
    if (hash(w.id + 500, Math.floor(frac(t / LOOP + w.off) * w.n), 43) >= w.p) continue;
    ctx.fillStyle = w.col;
    for (const [x, y0, y1] of w.spans) ctx.fillRect(x, y0, 1, y1 - y0 + 1);
  }
  for (const b of BEACONS) if (frac(t / 2 + b.ph) < 0.3) { px(ctx, b.x, b.y, '#ff4a4a'); halo(HL.red, b.x, b.y, 0.7); }
  // Telefónica's crown softly floodlit
  halo(HL.crown, 387, 81, 0.5 + 0.05 * wave(t, 8));
  for (const l of ROOF_LAMPS) halo(HL.roofLamp, l.x, l.y, 0.85 + 0.15 * wave(t, 900 + l.x));
  const tt = TURRET, tb = Math.round(fy(tt.z, 1.2));
  halo(HL.roofLamp, tt.x, tb - 13, 0.9);
  // blade signs: letters glow, bulbs chase round the edge
  BLADES.forEach((b, k) => {
    let m = 0.92 + 0.08 * wave(t, 1800 + k * 300);
    if (k === 0 && SIGN_BUZZ.some(([a, e]) => t >= a && t < e)) { const hv = hash(k, Math.floor(t * 28), 5); m = hv > 0.45 ? 1 : hv > 0.2 ? 0.35 : 0; }
    for (let j = 0; j < b.word.length; j++) {
      const g = FONT3[b.word[j]];
      g.forEach((row, jy) => { for (let ix = 0; ix < 3; ix++) if (row[ix] === '1') px(ctx, b.x + 1 + ix, b.y + 1 + j * 6 + jy, m > 0.6 ? b.col : m > 0.2 ? '#6a3040' : '#2a1820'); });
    }
    halo(HL.blade[k], b.x + 2, b.y + b.word.length * 3, m);
    const bulbs = BLADE_BULBS[k], ph = step(t, 1440 + k * 240);
    for (let i = 0; i < bulbs.length; i++) px(ctx, bulbs[i][0], bulbs[i][1], (i + ph) % 3 === 0 ? '#fff4c8' : '#6a4a30');
  });
  // the theatre marquee
  const mp = step(t, 1680);
  for (let i = 0; i < MARQUEE.length; i++) {
    const on = (i + mp) % 4 < 2;
    px(ctx, MARQUEE[i][0], MARQUEE[i][1], on ? '#fff0b8' : '#8a6a3a');
  }
  halo(HL.marquee, (MARQUEE[0][0] + MARQUEE[MARQUEE.length - 1][0]) >> 1, MARQUEE[0][1] + 2, 0.9);
  // far rooftop neon: steady, with a stuck letter that comes and goes
  const hs = hash(3, step(t, 240), 17) < 0.25 ? 3 : -1;
  for (let j = 0; j < 6; j++) text(ctx, 'HOSTAL'[j], HOSTAL.x + j * 4, HOSTAL.y, j === hs ? '#4a1c24' : '#ff6a5a');
  halo(HL.hostal, HOSTAL.x + 11, HOSTAL.y + 2, 0.85);
  // the chemist's cross: pulses green in two beats
  const pf = frac(t / 2);
  const pc = pf < 0.5 ? 1 : pf < 0.75 ? 0.35 : 0.75;
  ctx.fillStyle = pc > 0.6 ? '#5aff8a' : '#2a8a4a';
  ctx.fillRect(PHARM.x - 1, PHARM.y - 2, 3, 5); ctx.fillRect(PHARM.x - 2, PHARM.y - 1, 5, 3);
  px(ctx, PHARM.x, PHARM.y, '#d8ffe0');
  halo(HL.pharm, PHARM.x, PHARM.y, pc);
}

/* ================= San José's bell (it swings for each toll) ================= */
const TOLLS = [44, 47, 50, 164, 167, 170, 173];
function bellSwing(t) {                               // -1..1 swing, 0 at rest; a toll sounds at each extreme
  for (const s of TOLLS) {
    const u = t - s + 1.5;
    if (u >= 0 && u < 3) return Math.sin(Math.PI * u / 1.5 - Math.PI / 2) * env(t, s - 1.5, s + 1.5, 0.3);
  }
  return 0;
}
function drawBell(t) {
  const a = bellSwing(t), dx = Math.round(a * 1.4);
  const bx = BELL.x + dx, by = BELL.y;
  rect(ctx, BELL.x - 1, by - 1, 5, 1, '#3a2418');                                 // yoke
  if (Math.abs(a) > 0.7) {                            // swung up: the mouth turns towards the side
    rect(ctx, bx, by, 3, 3, '#3a2616'); px(ctx, bx + (a > 0 ? 3 : -1), by + 1, '#3a2616');
    px(ctx, bx + (a > 0 ? 0 : 2), by, '#e0a860');
  } else {
    rect(ctx, bx, by, 3, 3, '#3a2616'); rect(ctx, bx - 1, by + 3, 5, 1, '#4a321e');
    px(ctx, bx, by, '#d09a58'); px(ctx, bx - 1, by + 3, '#e8b870');
    px(ctx, bx + 1, by + 4, '#2a1a10');                // clapper
  }
  halo(HL.belfry, BELL.x + 1, BELL.y + 2, 0.7 + 0.3 * Math.abs(a));
}

/* ================= The Metrópolis ================= */
// Victory: wings raised, a laurel wreath held high (17 x 19, torso over column 8)
const VICTORY = [
  '.f.......GGG...F.', '.ff.....G...G.FF.', '.fff.....GGG.FFF.', '..fff.....H..FFF.', '..ffff....L.FFFF.',
  '...ffff...L.FFF..', '...ffffHM.LFFFF..', '....fffHMLMFFF...', '....ffLLMMMFFF...', '.....fLLMMDFF....',
  '......LLMMD......', '.......LMMD......', '.......LMMD......', '......HLMMMD.....', '......LLMMMD.....',
  '.....HLLMMMDD....', '.....LLMMMMMD....', '......LM.MMD.....', '.....DDDDDDD.....',
];
const VIC_C = { D: '#2a1e1a', M: '#6e4e30', L: '#b88a4e', H: '#f6d692', G: '#ffe27a' };
const VIC_Y = 42;                                     // top row of the statue
const STONE = ['#3a2e3e', '#5a4852', '#806862', '#a88a74', '#caa888', '#e6c89c', '#f6e2b4'];
const SLATE = ['#100e1a', '#171524', '#1f1c2e', '#2a2638', '#383448'];
const domeHW = y => {                                 // dome half width on row y (base y = 100, crown y = 71)
  const u = (100 - y) / 29;
  return u < 0 || u > 1 ? -1 : Math.max(3.5, 16 * Math.pow(1 - Math.pow(u, 2.2), 0.55));
};
const COLS = [-0.97, -0.84, -0.42, -0.26, 0.26, 0.42, 0.84, 0.97].map(s => Math.round(MX + MR * s - 0.5));   // paired columns
const GROUPS = [-0.9, -0.34, 0.34, 0.9].map(s => Math.round(MX + (MR + 0.5) * s));                            // statue groups over them
const GOLD = [];                                      // gilded pixels that can catch a sparkle
function renderMetropolis() {
  const P = painter(W, H);
  const shade = (n, h) => clamp(0.92 - 0.55 * Math.abs(n + 0.32) - (n > 0.5 ? (n - 0.5) * 0.7 : 0) + h, 0, 1);
  // the two wings, running back along Alcalá (left) and Gran Vía (right)
  for (const [side, z1] of [[AL_N, 1.35], [GV_S, 1.18]]) {
    const xa = sideX(side, 1), xb = sideX(side, z1);
    for (let i = Math.round(Math.min(xa, xb)); i <= Math.round(Math.max(xa, xb)); i++) {
      const z = sideZ(side, i + 0.5);
      if (z < 1 || z > z1) continue;
      const unit = (GR + CR) / z, bot = gy(z), top = Math.round(fy(z, 1)), period = PZ * Math.abs(side.off) / (z * z);
      const q = z / (PZ * 0.8), qf = q - Math.floor(q);
      for (let y = top - 3; y <= Math.round(bot); y++) {
        const h = (bot - y) / unit;
        let c;
        if (y < top) c = y === top - 3 ? '#e8cc9c' : (i & 1) ? '#b89a78' : '#1e1822';                 // balustrade
        else if (y === top) c = '#f4dcac';
        else if (y === top + 1) c = '#4a3a3c';
        else if (h < 0.2) c = h > 0.17 ? '#d8b88c' : h > 0.03 && qf > 0.2 && qf < 0.8 ? (h > 0.13 ? '#e8b878' : '#ffd8a0') : '#4a3a3a';
        else {
          c = pick(STONE, 0.3 + 0.45 * (1 - h) + (side === AL_N ? 0.1 : -0.12), i, y);
          const fp = frac((h - 0.2) / 0.155);
          if (period > 2.4 && qf > 0.3 && qf < 0.75 && fp > 0.2 && fp < 0.78) c = hash(Math.floor(q), Math.floor((h - 0.2) / 0.155), 9) < 0.35 ? '#ffd490' : '#2a2030';
          else if (Math.abs(h - 0.62) < 0.02) c = '#e0c294';
        }
        P.put(i, y, c);
      }
      if (hash(Math.floor(z / 0.09), 4, 12) < 0.5 && (i & 3) === 0) P.put(i, top - 4, '#f0d8a8');   // finials
    }
  }
  // the rotunda
  for (let i = MX - MR; i < MX + MR; i++) {
    const n = (i + 0.5 - MX) / MR, isCol = COLS.includes(i) || COLS.includes(i - 1);
    for (let y = MCORN; y <= MBASE; y++) {
      let c;
      if (y >= 170) {                                 // ground floor: arcaded shopfront
        const arch = Math.abs(frac((n + 1) * 2.5) - 0.5) < 0.3 && y > 172 + Math.round(1 + Math.cos(frac((n + 1) * 2.5) * TAU));
        c = arch ? (y > 176 ? '#ffd89a' : '#f0b870') : pick(STONE, shade(n, 0.05), i, y);
        if (y === MBASE) c = '#5a4a48';
      } else if (y >= 166) c = y === 166 ? '#fff0c8' : y === 169 ? '#4a3a3e' : pick(STONE, shade(n, 0.12), i, y);
      else if (y >= 139) {                            // two floors of windows with little iron balconies
        const wp = frac((n + 1) * 3.5), fl = y < 152 ? 0 : 1, yp = y - (fl ? 152 : 139);
        c = pick(STONE, shade(n, 0), i, y);
        if (Math.abs(wp - 0.5) < 0.2 && yp >= 3 && yp <= 10) c = yp === 10 ? '#1a141c' : hash(Math.floor((n + 1) * 3.5), fl, 21) < 0.4 ? (yp < 5 ? '#ffe2a8' : '#ffc878') : '#2a2232';
        if (y === 151) c = '#e6cc9c';
      } else if (y >= 136) c = y === 136 ? '#fff4d0' : y === 137 ? '#d8bc90' : '#3e3038';   // cornice band
      else if (y >= 114) {                            // the colonnade: paired giant columns before a shadowed loggia
        if (isCol) c = COLS.includes(i) ? pick(STONE, shade(n, 0.2), i, y) : pick(STONE, shade(n, -0.18), i, y);
        else {
          const wp = frac((n + 1) * 3.5);
          c = y < 117 ? '#2a2030' : Math.abs(wp - 0.5) < 0.22 && y > 118 && y < 133 ? (hash(Math.floor((n + 1) * 3.5), 5, 22) < 0.55 ? (y < 122 ? '#ffe6b0' : '#ffc070') : '#342a3e') : '#2e2434';
        }
      } else c = y === MCORN ? '#fff4d4' : y === MCORN + 1 ? '#e0c498' : y === MCORN + 2 ? '#a88a74' : '#4a3a40';   // entablature
      P.put(i, y, c);
    }
  }
  for (const cx of COLS) { P.put(cx, 113, '#fff0c8'); P.put(cx, 135, '#c8aa84'); }   // capitals and bases
  // cornice lip, balustrade, attic drum and the four statue groups
  for (let i = MX - MR - 1; i <= MX + MR; i++) { P.put(i, MCORN - 1, '#f8e4b8'); P.put(i, MCORN - 5, '#f0d8a8'); }
  for (let i = MX - MR; i < MX + MR; i++) for (let y = MCORN - 4; y < MCORN - 1; y++) P.put(i, y, (i & 1) ? pick(STONE, shade((i - MX) / MR, 0.15), i, y) : '#2a2030');
  for (let i = MX - 16; i < MX + 16; i++) for (let y = 100; y < MCORN - 5; y++) {
    const n = (i + 0.5 - MX) / 16;
    P.put(i, y, Math.abs(frac((n + 1) * 3) - 0.5) < 0.12 && y === 102 ? '#1a1420' : pick(STONE, shade(n, -0.05), i, y));
  }
  // the dome: black slate, gold ribs, gold garlands and crown
  GOLD.length = 0;
  for (let y = 71; y <= 100; y++) {
    const hw = domeHW(y);
    for (let i = Math.round(MX - hw); i < Math.round(MX + hw); i++) {
      const n = clamp((i + 0.5 - MX) / hw, -1, 1), th = Math.asin(n);
      let c = pick(SLATE, clamp(0.75 - 0.6 * Math.abs(n + 0.38) + (y > 92 ? 0.1 : 0), 0, 1), i, y);
      let gold = false;
      for (const phi of [-1.2, -0.6, 0, 0.6, 1.2]) if (Math.abs(th - phi) * hw < 0.62) gold = true;
      const u = (100 - y) / 29;
      if (Math.abs(u - 0.42) < 0.03 && ((i + y) & 1)) gold = true;                // a ring of gilded wreaths
      if (u < 0.12 && ((i & 1) || y > 98)) gold = true;                          // garland band at the base
      if (u > 0.9) gold = true;                                                  // the crown
      if (gold) { c = n < -0.15 ? '#ffe08a' : n < 0.45 ? '#e0a840' : '#8a6026'; GOLD.push([i, y]); }
      P.put(i, y, c);
    }
  }
  for (const g of GROUPS) {                          // the four statue groups, in front of the dome
    P.put(g - 1, MCORN - 6, '#caa888'); P.put(g, MCORN - 6, '#e6c89c'); P.put(g + 1, MCORN - 6, '#806862');   // pedestal
    for (let y = MCORN - 11; y < MCORN - 6; y++) { P.put(g - 1, y, y < MCORN - 9 ? '#5a4852' : '#a88a74'); P.put(g, y, y === MCORN - 11 ? '#806862' : '#caa888'); }
    P.put(g + 1, MCORN - 8, '#806862'); P.put(g + 1, MCORN - 7, '#5a4852'); P.put(g, MCORN - 12, '#a88a74');
  }
  // the lantern, the globe and Victory
  for (let y = 64; y < 71; y++) for (let i = MX - 3; i <= MX + 3; i++)
    P.put(i, y, (i - MX + 3) % 2 === 0 ? (i < MX ? '#ffe08a' : '#c08a36') : y > 65 && y < 70 ? '#ffd48a' : '#3a2a20');
  for (let i = MX - 2; i <= MX + 2; i++) P.put(i, 63, '#e0a840');
  for (const y of [61, 62]) { P.put(MX - 1, y, '#ffe08a'); P.put(MX, y, '#e0a840'); P.put(MX + 1, y, '#a87a30'); }
  VICTORY.forEach((row, j) => { for (let k = 0; k < 17; k++) {
    const ch = row[k];
    if (ch === '.') continue;
    let c = VIC_C[ch];
    if (ch === 'f') c = row[k - 1] === '.' ? '#e8c07a' : ((k + j) & 1) ? '#a8783e' : '#7a5632';
    if (ch === 'F') c = row[k + 1] === '.' ? '#7a5a3a' : ((k + j) & 1) ? '#3a2a1e' : '#4e3826';
    P.put(MX - 8 + k, VIC_Y + j, c);
    if (ch === 'G' || ch === 'H' || (ch === 'f' && row[k - 1] === '.')) GOLD.push([MX - 8 + k, VIC_Y + j]);
  } });
  return P.done();
}
// two bats hunting moths round the lamps in front of the Metrópolis
const BATS = [{ cx: 176, cy: 158, rx: 22, ry: 12, k1: 23, k2: 37, p: 0.1 }, { cx: 232, cy: 150, rx: 26, ry: 14, k1: 19, k2: 31, p: 0.6 }];
function drawBats(t) {
  for (const b of BATS) {
    const x = Math.round(b.cx + b.rx * wave(t, b.k1, b.p) + 5 * wave(t, b.k2 * 3, b.p * 2));
    const y = Math.round(b.cy + b.ry * wave(t, b.k2, b.p + 0.25) + 3 * wave(t, b.k1 * 4, b.p));
    const up = step(t, 4800) & 1;
    ctx.fillStyle = '#140c16';
    ctx.fillRect(x, y, 1, 1);
    if (up) { ctx.fillRect(x - 2, y - 1, 2, 1); ctx.fillRect(x + 1, y - 1, 2, 1); }
    else { ctx.fillRect(x - 2, y, 2, 1); ctx.fillRect(x + 1, y, 2, 1); ctx.fillRect(x - 3, y + 1, 1, 1); ctx.fillRect(x + 3, y + 1, 1, 1); }
  }
}
function drawMetropolisFx(t) {
  halo(HL.domeWash, MX, 88, 0.9 + 0.05 * wave(t, 4));
  halo(HL.victory, MX, VIC_Y + 9, 0.8 + 0.1 * wave(t, 6));
  halo(HL.lantern, MX, 67, 0.85 + 0.15 * wave(t, 1400));
  // gold leaf catching the floodlights: a few sparkles at a time
  const s = step(t, 2400);
  for (let k = 0; k < 3; k++) {
    const g = GOLD[Math.floor(hash(k, s, 77) * GOLD.length)];
    if (!g) continue;
    px(ctx, g[0], g[1], '#ffffff');
    if (hash(k, s, 78) < 0.4) halo(HL.spark, g[0], g[1], 0.8);
  }
  // a slow glint climbing the central rib every 12 s
  const u = frac(t / 12);
  if (u < 0.18) { const y = Math.round(100 - u / 0.18 * 28); px(ctx, MX, y, '#fffbe8'); px(ctx, MX - 1, y, '#fff0c0'); halo(HL.spark, MX, y, 1 - u / 0.18); }
  // warm light in the loggia and the shop arcades
  halo(HL.arcade, MX - 6, 178, 0.75 + 0.05 * wave(t, 700));
}

/* ================= Street lamps (ornate farolas on the plaza, smaller ones down both streets) ================= */
const FAROLAS = [{ x: 176, y: 194 }, { x: 228, y: 194 }];      // three-lantern lamp posts before the rotunda
const LAMPS = [];                                     // { x, y (lantern), z }
{
  const add = (side, off, zs) => { for (const z of zs) LAMPS.push({ x: Math.round(side.vp + off / z), y: Math.round(gy(z) - 20 / z), z }); };
  add(AL_N, 68, [1.3, 1.8, 2.6, 3.8, 6]);
  add(GV_S, -145, [1.35, 1.9, 2.7, 4, 6.5]);
  add(GV_N, 16, [1.2, 1.8, 3]);
  add(AL_S, -50, [1.0, 1.5, 2.3, 3.6]);
}
// the streets' pavements (units: at depth z a unit is 1 / z px across; the walls stand at 91 / -70 and -171 / 40)
const WALKS = { aln: [64, 91], als: [-70, -46], gvs: [-171, -140], gvn: [12, 40] };
function groundLight(i, y) {                          // warm pools of lamplight on the pavement, 0..1
  let v = 0;
  for (const l of LAMPS) {
    const s = 1 / l.z, d = Math.hypot((i - l.x) / (34 * s), (y - gy(l.z)) / (9 * s));
    if (d < 1) v = Math.max(v, (1 - d) ** 1.4);
  }
  for (const f of FAROLAS) { const d = Math.hypot((i - f.x) / 38, (y - f.y - 3) / 13); if (d < 1) v = Math.max(v, (1 - d) ** 1.3 * 1.1); }
  for (const [lx, ly] of [[112, 244], [300, 246], [420, 244]]) {                // lamps below our balcony light the near lanes
    const d = Math.hypot((i - lx) / 90, (y - ly) / 26);
    if (d < 1) v = Math.max(v, (1 - d) * 0.75);
  }
  const cafe = Math.hypot((i - 151) / 26, (y - 193) / 8);
  if (cafe < 1) v = Math.max(v, (1 - cafe) * 0.9);
  const shop = Math.hypot((i - 262) / 30, (y - 178) / 8);
  if (shop < 1) v = Math.max(v, (1 - shop) * 0.6);
  return Math.min(1, v);
}
const ASPH = ['#141220', '#1a1626', '#211b2c', '#2a2132', '#352836', '#44303a', '#583c3e', '#6e4a44'];
const PAVE = ['#221c2a', '#2c2430', '#382c36', '#48383a', '#5c4640', '#725648', '#8a6850', '#a07a5a'];
function renderStreet() {
  const P = painter(W, H), inW = (X, [a, b]) => X > a && X < b;
  for (let y = HZ + 1; y < H; y++) {
    const z = GR / (y - HZ);
    for (let i = 0; i < W; i++) {
      const XL = (i + 0.5 - VPL) * z, XR = (i + 0.5 - VPR) * z, L = groundLight(i, y);
      const plaza = z > CURB && z < 1.12 && XL > 40 && XR < -120;
      // our side of Alcalá and Gran Vía's far side end at the cross street, turning the corner in front of their buildings
      const corner = z > CURB && (inW(XL, WALKS.als) || inW(XR, WALKS.gvn) || (z < CORNER && (XL < WALKS.als[1] || XR > WALKS.gvn[0])));
      const walk = plaza || corner || (z >= 1 && (inW(XL, WALKS.aln) || inW(XR, WALKS.gvs)));
      let c;
      if (walk) {                                     // granite slabs, lit by lamps and by the shop windows along the wall
        const shopLit = (XL > 82 || XR < -162 || XL < -62 || XR > 32 || (corner && z > CORNER - 0.025)) ? 0.25 : 0;
        const edge = ((plaza || corner) && z < CURB + 0.015)
          || (!plaza && (Math.abs(XL - 64) < z * 0.7 || Math.abs(XR + 140) < z * 0.7 || Math.abs(XL + 46) < z * 0.7 || Math.abs(XR - 12) < z * 0.7));
        const slab = ((Math.floor(XL / 4) + Math.floor(z * 24)) & 1) === 0;
        c = edge ? pick(['#4a4048', '#8a7a70', '#b09a84'], L + 0.3, i, y) : pick(PAVE, 0.2 + L * 0.75 + shopLit + (slab ? 0.05 : 0), i, y);
      } else {
        c = pick(ASPH, 0.28 + L * 0.72 - Math.max(0, (y - 236) / 140), i, y);
        const dash = frac(z * 5) < 0.45, line = L > 0.3 ? '#a8987e' : '#6a5e62';
        if (z > 1.24) {                               // lanes running away down both streets
          if (Math.abs(XL - 9) < 0.55 * z || Math.abs(XR + 64) < 0.55 * z) c = line;
          else if (dash && (Math.abs(XL + 19) < 0.5 * z || Math.abs(XL - 37) < 0.5 * z || Math.abs(XR + 102) < 0.5 * z || Math.abs(XR + 26) < 0.5 * z)) c = line;
        }
        // zebra crossings over the mouths of both streets
        if ((z > 1.13 && z < 1.23 && XL > -46 && XL < 64 && frac(XL / 7) < 0.5) || (z > 1.15 && z < 1.26 && XR > -140 && XR < 12 && frac(XR / 7) < 0.5))
          c = pick(['#6a6270', '#9a9298', '#c8bcb4'], 0.3 + L, i, y);
        if (y === 197 || ((y === 214 || y === 234) && ((i >> 3) & 1))) c = L > 0.3 ? '#9a8a7a' : '#5a5260';      // lanes before us
      }
      P.put(i, y, c);
    }
  }
  return P.done();
}
function renderFarola(x, y) {                         // cast-iron post with three glass lanterns (drawn into the street props)
  const [c, g] = makeCanvas(17, 34);
  const R = (a, b, w, h, col) => rect(g, a - x + 8, b - y + 33, w, h, col);
  R(x - 1, y - 3, 3, 3, '#2a2230'); R(x - 2, y - 1, 5, 1, '#3a3040');
  R(x, y - 24, 1, 21, '#1e1824'); R(x - 1, y - 12, 3, 1, '#3a3040');
  R(x - 6, y - 24, 13, 1, '#241c2a'); R(x - 6, y - 25, 1, 1, '#241c2a'); R(x + 6, y - 25, 1, 1, '#241c2a');
  for (const [lx, ly] of [[x - 6, y - 29], [x + 6, y - 29], [x, y - 31]]) {
    R(lx - 1, ly, 3, 4, '#ffe6b0'); R(lx, ly + 1, 1, 2, '#fffbe8'); R(lx - 1, ly - 1, 3, 1, '#2a2230'); R(lx, ly - 2, 1, 1, '#2a2230');
    R(lx - 1, ly + 4, 3, 1, '#2a2230');
  }
  return c;
}
const TLIGHTS = [{ x: 142, y: 195 }, { x: 258, y: 195 }];
function tlState(t) {                                 // the junction's lights: 40 s cycle (green, amber, red)
  const f = frac(t / 40);
  return f < 0.58 ? 2 : f < 0.66 ? 1 : 0;
}
function drawTrafficLights(t) {
  const s = tlState(t), walk = s === 0 && frac(t / 40) < 0.95, blink = frac(t / 40) > 0.9 && s === 0;
  for (const l of TLIGHTS) {
    rect(ctx, l.x, l.y - 14, 1, 14, '#1e1a24');
    rect(ctx, l.x - 1, l.y - 19, 3, 6, '#141018');
    const cols = ['#ff3a3a', '#ffb030', '#3aff9a'];
    for (let k = 0; k < 3; k++) px(ctx, l.x, l.y - 18 + k * 2, s === k ? cols[k] : '#2a2228');
    halo(HL.tl[s], l.x, l.y - 18 + s * 2, 0.8);
    rect(ctx, l.x - 1, l.y - 11, 3, 3, '#141018');     // the walker below it
    px(ctx, l.x, l.y - 10, walk && !(blink && frac(t * 2) < 0.5) ? '#5aff9a' : '#ff4a3a');
  }
}

/* ================= The terrace café on the plaza ================= */
const PARASOLS = [{ x: 144, y: 194 }, { x: 159, y: 194 }];
const CAFE_BULBS = [];
for (let i = 138; i <= 166; i += 3) CAFE_BULBS.push({ x: i, y: 185 + Math.round(1.5 * Math.sin((i - 138) / 28 * Math.PI * 2 + 0.2) ** 2), k: 30 + (i % 7) * 9, ph: hash(i, 3) });
function renderCafe() {
  const [c, x] = makeCanvas(W, H);
  for (const p of PARASOLS) {
    rect(x, p.x - 2, p.y - 4, 5, 1, '#6a4a3a'); px(x, p.x, p.y - 3, '#2a1e1e');             // table
    rect(x, p.x, p.y - 11, 1, 7, '#8a7a6a');                                                // pole
    rect(x, p.x - 2, p.y - 14, 5, 1, '#e8d8bc'); rect(x, p.x - 4, p.y - 13, 9, 1, '#d8c4a4'); rect(x, p.x - 5, p.y - 12, 11, 1, '#ffe6bc');
    for (let k = -5; k <= 5; k += 2) px(x, p.x + k, p.y - 11, '#b8403a');                    // scalloped red trim
    for (const s of [-1, 1]) {                                                               // two people at each table
      const cx = p.x + s * 4;
      px(x, cx, p.y - 7, '#d8a078'); rect(x, cx, p.y - 6, 1, 2, s < 0 ? '#3a5a8a' : '#c8584a'); px(x, cx, p.y - 4, '#2a2230');
      px(x, cx - s, p.y - 4, '#4a3a34'); px(x, cx - s, p.y - 3, '#4a3a34');                 // chair back
    }
  }
  rect(x, 167, 186, 1, 8, '#2a2230');                                                        // pole carrying the string lights
  for (let i = 138; i <= 166; i++) px(x, i, 184 + Math.round(1.5 * Math.sin((i - 138) / 28 * Math.PI * 2 + 0.2) ** 2), '#2a2028');
  return c;
}
function drawCafe(t) {
  halo(HL.cafe, 151, 189, 0.85 + 0.1 * wave(t, 900));
  for (const b of CAFE_BULBS) {
    const I = 0.6 + 0.4 * wave(t, b.k, b.ph);
    ctx.globalAlpha = 0.5 + 0.5 * I; px(ctx, b.x, b.y + 1, '#ffd890');
    halo(HL.bulb, b.x, b.y + 1, I * 0.7);
  }
  ctx.globalAlpha = 1;
  // the waiter crossing between the tables and the bar
  const u = frac(t / 16), wx = Math.round(lerp(136, 168, 0.5 - 0.5 * Math.cos(u * TAU))), fr = Math.floor(t * 4) & 1;
  px(ctx, wx, 186, '#d8a078'); rect(ctx, wx, 187, 1, 3, '#f0ece4'); rect(ctx, wx, 190, 1, 2, '#1a1620');
  if (fr) px(ctx, wx + 1, 191, '#1a1620'); else px(ctx, wx - 1, 191, '#1a1620');
  px(ctx, wx + (u < 0.5 ? 1 : -1), 187, '#c8c8d0');                                         // the tray
}

/* ================= Headlights streaming down both streets ================= */
const STREAMS = [
  { vp: VPL, X: 36, out: true, n: 9, cars: 9, seed: 1 }, { vp: VPL, X: -22, out: false, n: 8, cars: 8, seed: 2 },
  { vp: VPR, X: -40, out: true, n: 10, cars: 10, seed: 3 }, { vp: VPR, X: -112, out: false, n: 9, cars: 10, seed: 4 },
];
function drawStreams(t) {
  for (const s of STREAMS) for (let k = 0; k < s.cars; k++) {
    const { cyc, u } = cycle(t, s.n, (k + hash(k, s.seed, 5) * 0.6) / s.cars);
    if (hash(k, cyc, s.seed + 9) < 0.2) continue;
    const uu = s.out ? u : 1 - u, z = lerp(0.98, 13, uu * uu);
    const x = s.vp + s.X / z, g = gy(z), yl = Math.round(g - 1.8 / z);
    const a = smooth(0, 0.06, s.out ? u : 1 - u) * (1 - smooth(0.9, 1, s.out ? u : 1 - u)) * clamp(1.3 - z / 12, 0.25, 1);
    if (a <= 0.02) continue;
    const sep = 4.2 / z, col = s.out ? '#ff3a3a' : '#fff2cc';
    if (z < 1.7) {                                    // near enough to see the car itself
      const L = Math.round(10 / z), Hh = Math.max(2, Math.round(4 / z));
      ctx.globalAlpha = a; rect(ctx, Math.round(x - L / 2), Math.round(g - Hh), L, Hh, '#15121c');
      rect(ctx, Math.round(x - L / 4), Math.round(g - Hh), Math.round(L / 2), 1, '#3a3446');
    }
    ctx.globalAlpha = a;
    if (sep >= 1.5) { px(ctx, Math.round(x - sep / 2), yl, col); px(ctx, Math.round(x + sep / 2), yl, col); }
    else px(ctx, Math.round(x), yl, col);
    ctx.globalAlpha = 1;
    if (z < 3) halo(s.out ? HL.tail : HL.head, x, yl, a * (1.2 - z / 3));
  }
  ctx.globalAlpha = 1;
}
// tiny figures walking along the far pavements of Gran Vía
const STROLL = [];
{ const r = rng(505); for (let i = 0; i < 14; i++) STROLL.push({ id: i, X: r() < 0.5 ? -156 + Math.floor(r() * 10) : 22 + Math.floor(r() * 10), n: 3 + Math.floor(r() * 4), ph: r(), out: r() < 0.5, col: ['#c8584a', '#e8dcc0', '#4a86c8', '#f0c040', '#8a52b8'][Math.floor(r() * 5)] }); }
function drawStrollers(t) {
  for (const p of STROLL) {
    const { u } = cycle(t, p.n, p.ph), uu = p.out ? u : 1 - u, z = lerp(1.25, 7, uu);
    const x = Math.round(VPR + p.X / z), g = Math.round(gy(z));
    if (x > SHUT) continue;
    const h = z < 2 ? 3 : z < 3.6 ? 2 : 1;
    ctx.globalAlpha = smooth(0, 0.08, u) * (1 - smooth(0.92, 1, u));
    rect(ctx, x, g - h, 1, h, p.col);
    if (h === 3) px(ctx, x, g - 4, '#d8a078');
  }
  ctx.globalAlpha = 1;
}
// people on the plaza before the Metrópolis
const WALKERS = [
  { n: 5, ph: 0.1, dir: 1, shirt: '#d8584a', y: 193 }, { n: 4, ph: 0.55, dir: -1, shirt: '#e8dcc0', y: 190 },
  { n: 6, ph: 0.3, dir: 1, shirt: '#4a86c8', y: 191, pair: '#e07ab0' }, { n: 7, ph: 0.8, dir: -1, shirt: '#f0c040', y: 194 },
  { n: 3, ph: 0.68, dir: -1, shirt: '#8a52b8', y: 192, dog: true }, { n: 5, ph: 0.42, dir: 1, shirt: '#3aa888', y: 189 },
];
function drawPerson(x, y, dir, shirt, fr) {           // 2 px wide, 6 px tall, feet on row y
  px(ctx, x + (dir > 0 ? 1 : 0), y - 5, '#d8a078');
  px(ctx, x + (dir > 0 ? 0 : 1), y - 5, '#2a1e1e');
  rect(ctx, x, y - 4, 2, 2, shirt);
  rect(ctx, x, y - 2, 2, 1, '#2a2a3c');
  ctx.fillStyle = '#1e1a24';
  if (fr) { ctx.fillRect(x - 1, y, 1, 1); ctx.fillRect(x + 2, y, 1, 1); ctx.fillRect(x, y - 1, 2, 1); }
  else ctx.fillRect(x, y - 1, 2, 2);
}
function drawWalkers(t) {
  for (const w of WALKERS) {
    const { u } = cycle(t, w.n, w.ph);
    const x = Math.round(w.dir > 0 ? lerp(128, 272, u) : lerp(272, 128, u));
    ctx.globalAlpha = smooth(0, 0.06, u) * (1 - smooth(0.94, 1, u));
    const fr = Math.floor(t * 4) & 1;
    drawPerson(x, w.y, w.dir, w.shirt, fr);
    if (w.pair) drawPerson(x - 3 * w.dir, w.y + 1, w.dir, w.pair, fr ^ 1);
    if (w.dog) {
      const dx = x - 5 * w.dir;
      rect(ctx, dx, w.y - 2, 3, 1, '#7a5638'); px(ctx, dx + (w.dir > 0 ? 3 : -1), w.y - 3, '#7a5638');
      px(ctx, dx + (fr ? 0 : 1), w.y - 1, '#5a3e2a'); px(ctx, dx + (fr ? 2 : 1), w.y - 1, '#5a3e2a');
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Traffic in the lanes below the balcony ================= */
// The street is a few floors down, so its traffic shows through the railing's open middle (rows 218-234): below the
// handrail and the ring band, clear of the lower band and the floor. The strip seen over the handrail is the plaza's
// kerbside lane, kept empty so nothing seems to run along the rail. Lanes: wheel row (the car's near side), depth
// (sets the size), direction (traffic keeps right, so the far lane runs left), crossings per loop and slots.
const LANES = [
  { y: 226, z: GR / (226 - HZ), dir: -1, n: 9, slots: 4, seed: 1 },     // the far lane
  { y: 232, z: GR / (232 - HZ), dir: 1, n: 11, slots: 3, seed: 2 },      // the near lane: buses, the sweeper, the ambulance
];
const PATH = W + 160;                                 // vehicles run from 80 px off one side to 80 px off the other
const CAR_COLS = [['#6a6a78', '#3a3a48'], ['#8a2a30', '#4a1a20'], ['#2a4a80', '#16264a'], ['#9a9080', '#5a5448'],
  ['#2a2a34', '#16161e'], ['#c8c8cc', '#7a7a84'], ['#3a5a4a', '#1e3228']];
// Every sprite faces right. From a few floors up a vehicle shows its side and, above it, its top foreshortened to
// topRows(s) rows: hood, roof and boot, a bus's roof with its vents. Lamps are [x, y] inside the sprite (the near ones;
// the far ones sit at the far corners, topRows higher); a roof light or beacon sticks up from row 0.
const topRows = s => Math.round(1.7 * s);
const lit = (col, k = 0.3) => mix(col, '#ffe6c0', k);   // a top face or an edge catching the street lamps
function lamps(x, w, near, far) {
  px(x, w - 1, near, '#fff6d8'); px(x, w - 1, far, '#e8d8b0'); px(x, 0, near, '#ff3a3a'); px(x, 0, far, '#b8262a');
}
function makeCar(s, body, dark, taxi) {
  const L = Math.round(11.4 * s), d = topRows(s), hc = Math.round(1.1 * s), hb = Math.round(1.2 * s);
  const yB = d + hc, Hh = yB + hb + 1, [c, x] = makeCanvas(L, Hh);    // roof, side windows, doors from yB, wheels
  const paint = taxi ? '#e8e8e0' : body, shade = taxi ? '#a8a8a4' : dark;
  const c0 = Math.round(L * 0.26), c1 = Math.round(L * 0.68), ws = Math.max(2, Math.round(L * 0.12));
  rect(x, 0, hc, c0, d, lit(paint)); rect(x, c1 + ws, hc, L - c1 - ws, d, lit(paint));                 // boot and hood
  rect(x, c0 + 1, 0, c1 - c0 - 1, d, lit(paint)); rect(x, c0 + 1, d - 1, c1 - c0 - 1, 1, lit(paint, 0.5));   // roof
  for (let k = -1; k < ws; k++) {                     // the rear window (k = -1) and the windscreen slope down from it
    const gx = k < 0 ? c0 : c1 + k, y0 = k < 0 ? 1 : Math.round(hc * (k + 0.5) / ws);
    rect(x, gx, y0, 1, yB - y0, '#1e2436'); px(x, gx, y0, '#56668a');
  }
  rect(x, c0, d, c1 + ws - c0, hc, '#161c2c'); px(x, c1 + ws - 2, d, '#4a5a7a');                         // side windows
  rect(x, Math.round((c0 + c1) / 2), d, 1, hc, shade);
  rect(x, 0, yB, L, hb, paint); rect(x, 0, yB, L, 1, lit(paint, 0.4)); rect(x, 0, yB + hb - 1, L, 1, shade);   // doors
  rect(x, 1, Hh - 1, L - 2, 1, '#0c0a10');
  const ww = Math.max(2, Math.round(L * 0.13));
  rect(x, Math.round(L * 0.14), Hh - 1, ww, 1, '#050408'); rect(x, Math.round(L * 0.72), Hh - 1, ww, 1, '#050408');
  if (taxi) {                                         // the red diagonal on the front door, the green roof light
    for (let k = 0; k < hb; k++) { px(x, c1 - 2 + k, yB + hb - 1 - k, '#d8203a'); px(x, c1 - 1 + k, yB + hb - 1 - k, '#d8203a'); }
    px(x, c1 - 2, 1, '#3aff7a');
  }
  lamps(x, L, yB, hc);
  return { c, w: L, h: Hh, front: [L - 1, yB], back: [0, yB], light: taxi ? [c1 - 2, 1] : null };
}
function makeBus(s) {                                 // a blue night bus with lit windows and its route on the side
  const L = Math.round(30 * s), H0 = Math.round(8.1 * s) + 1, o = topRows(s) - 1, Hh = H0 + o, [c, x] = makeCanvas(L, Hh);
  const R = (i, y, w, h, col) => rect(x, i, y + o, w, h, col), wb = Math.round(H0 * 0.55);   // the side, below its roof
  rect(x, 1, 0, L - 2, o, '#7e8494'); rect(x, 1, 0, L - 2, 1, '#5e6474');                  // the roof from above
  rect(x, Math.round(L * 0.3), 0, 4, o, '#4a4e5a'); rect(x, Math.round(L * 0.6), 0, 5, o, '#4a4e5a');   // vents
  R(1, 0, L - 2, 1, '#c8ccd4');
  R(0, 1, L, H0 - 2, '#2a5aa8');
  R(1, 2, L - 4, wb - 2, '#ffe2a0');
  for (let i = 1; i < L - 3; i += 5) R(i, 2, 1, wb - 2, '#1e3a70');
  for (let i = 3; i < L - 6; i += 5) if (hash(i, 7, 91) < 0.55) R(i + 1, wb - 3, 1, 2, '#3a2a30');      // passengers
  R(0, wb, L, 1, '#4a7ac8'); R(0, H0 - 3, L, 1, '#e8e8ee');
  R(L - 3, 1, 3, wb, '#1c2a44'); R(L - 2, 1, 1, 1, '#6a8ac0');                                           // windscreen
  if (H0 < 12) R(L - 12, 1, 7, 1, '#ffb030');
  R(0, H0 - 2, L, 1, '#16264a'); R(1, H0 - 1, L - 2, 1, '#0c0a10');
  for (const f of [0.14, 0.78]) R(Math.round(L * f), H0 - 1, Math.max(3, Math.round(L * 0.08)), 1, '#050408');
  lamps(x, L, H0 - 3 + o, H0 - 3);
  return { c, w: L, h: Hh, front: [L - 1, H0 - 3 + o], back: [0, H0 - 3 + o], bus: true, side: o };
}
function makeSweeper(s) {                             // the city's street sweeper: cab, tank, brushes
  const L = Math.round(14 * s), H0 = Math.round(6.5 * s) + 1, d = topRows(s), Hh = H0 + d, [c, x] = makeCanvas(L, Hh);
  const R = (i, y, w, h, col) => rect(x, i, y + d, w, h, col), cx = Math.round(L * 0.68);
  rect(x, 1, 1, cx - 2, d, '#c4c8c0'); rect(x, cx, 1, L - cx, d, lit('#f4f4ee', 0.15));               // tank top and cab roof
  rect(x, cx - 1, 1, 1, d, '#8a8e88'); rect(x, 1, 1, cx - 2, 1, '#9a9e98');
  R(0, 2, cx, H0 - 3, '#e8ece4'); R(0, Math.round(H0 * 0.55), cx, 1, '#2a8a5a'); R(1, 1, cx - 2, 1, '#dcdcd4');
  R(cx, 1, L - cx, H0 - 2, '#f4f4ee'); R(cx + 1, 2, L - cx - 2, 2, '#1c2436');
  R(0, H0 - 2, L, 1, '#5a6058'); R(1, H0 - 1, L - 2, 1, '#0c0a10');
  lamps(x, L, 3 + d, 3);
  return { c, w: L, h: Hh, front: [L - 1, 3 + d], back: [0, 3 + d], beacon: [cx + 1, 0] };
}
function makeAmbulance(s) {                           // white van with a chequered band; the blue lights flash per frame
  const L = Math.round(12.5 * s), H0 = Math.round(6 * s) + 1, d = topRows(s), Hh = H0 + d, [c, x] = makeCanvas(L, Hh);
  const R = (i, y, w, h, col) => rect(x, i, y + d, w, h, col), band = Math.round(H0 * 0.6);
  rect(x, 0, 1, L - 3, d, '#e4e4dc'); rect(x, 0, 1, L - 3, 1, '#c4c4bc');                              // the roof from above
  rect(x, L - 3, 2, 3, d - 1, '#2a3246'); px(x, L - 3, 2, '#5a6a8a');                                   // windscreen, sloping
  R(0, 1, L, H0 - 2, '#f0f0e8'); R(1, 1, L - 3, 1, '#ffffff');
  for (let i = 0; i < L; i++) R(i, band, 1, 1, (i >> 1) & 1 ? '#f8a020' : '#2a5ad8');
  R(L - 4, 2, 3, 2, '#1c2436');
  R(0, H0 - 2, L, 1, '#9a9a94'); R(1, H0 - 1, L - 2, 1, '#0c0a10');
  lamps(x, L, band - 1 + d, band - 1);
  return { c, w: L, h: Hh, front: [L - 1, band - 1 + d], back: [0, band - 1 + d], blues: [[2, 0], [L - 6, 0]] };
}
function mirror(v) {                                  // the same vehicle facing left
  const [c, x] = makeCanvas(v.w, v.h);
  x.scale(-1, 1); x.drawImage(v.c, -v.w, 0); x.setTransform(1, 0, 0, 1, 0, 0);
  const fl = p => p && [v.w - 1 - p[0], p[1]];
  return { ...v, c, front: fl(v.front), back: fl(v.back), light: fl(v.light), beacon: fl(v.beacon), blues: v.blues && v.blues.map(fl) };
}
/** the light a lane's vehicles throw on the asphalt over their footprint (rows y - h + 2 .. y + 1), additive: the
 *  headlamps' beam ahead and the tail lamps' red glow behind, each as [facing right, facing left] */
function makeBeams(s) {
  const d = topRows(s), h = d + 3, rc = (h - 1) / 2;
  const sprite = (len, col, alpha) => {
    const [c, x] = makeCanvas(len, h), img = x.createImageData(len, h), D = img.data, rgb = hex(col);
    for (let y = 0; y < h; y++) for (let i = 0; i < len; i++) {
      const q = Math.floor(alpha(i / len, Math.abs(y - rc)) * 10 + bayer(i, y)) / 10, o = (y * len + i) * 4;
      if (q <= 0) continue;
      D[o] = rgb[0]; D[o + 1] = rgb[1]; D[o + 2] = rgb[2]; D[o + 3] = Math.round(q * 255);
    }
    x.putImageData(img, 0, 0);
    return [c, mirror({ c, w: len, h }).c];
  };
  const BL = Math.round(22 * s), TL = Math.round(5 * s);
  return {
    head: sprite(BL, '#ffecc4', (u, dy) => 0.3 * (1 - u) ** 1.6 * clamp(1 - dy / (d / 2 + 0.6 + u * 1.4), 0, 1) ** 0.6), BL,
    tail: sprite(TL, '#ff3020', (u, dy) => 0.32 * u * clamp(1 - dy / (d / 2 + 0.5), 0, 1)), TL, h,
  };
}
// scheduled vehicles; the lane's other traffic keeps clear of them. The near lane is the kerbside one on our side, where
// the night buses, the sweeper and the ambulance run
const SPECIALS = [
  { lane: 1, kind: 'bus', a: 28, b: 54 }, { lane: 1, kind: 'bus', a: 184, b: 208 },
  { lane: 1, kind: 'sweeper', a: 60, b: 110 }, { lane: 1, kind: 'ambulance', a: 116, b: 121.5 },
];
function label(v, dir) {                              // the route on the side, written after any mirroring
  if (v.h - v.side < 12) return v;
  const x = v.c.getContext('2d'), bx = dir > 0 ? v.w - 17 : 4;
  rect(x, bx, 2 + v.side, 13, 7, '#0e0c10'); text(x, 'N16', bx + 1, 3 + v.side, '#ffb030');
  return v;
}
function buildVehicles() {
  return LANES.map((L, li) => {
    const s = 1 / L.z, face = v => (L.dir > 0 ? v : mirror(v)), runs = kind => SPECIALS.some(sp => sp.lane === li && sp.kind === kind);
    return {
      cars: CAR_COLS.map(([b, d]) => face(makeCar(s, b, d, false))), taxi: face(makeCar(s, '#e8e8e0', '#b0b0ac', true)), beams: makeBeams(s),
      bus: runs('bus') ? label(face(makeBus(s)), L.dir) : null,
      sweeper: runs('sweeper') ? face(makeSweeper(s)) : null, ambulance: runs('ambulance') ? face(makeAmbulance(s)) : null,
    };
  });
}
const laneX = (L, u) => (L.dir > 0 ? lerp(-80, W + 80, u) : lerp(W + 80, -80, u));
function drawVehicle(v, L, x, t, B) {
  const x0 = Math.round(x - v.w / 2), y0 = L.y - v.h + 1, f = L.dir > 0 ? 0 : 1, by = L.y - B.h + 2;
  ctx.globalCompositeOperation = 'lighter';            // its lights on the road ahead and behind, under the railing
  ctx.drawImage(B.head[f], f ? x0 - B.BL : x0 + v.w, by); ctx.drawImage(B.tail[f], f ? x0 + v.w : x0 - B.TL, by);
  ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(v.c, x0, y0);
  const fx = x0 + v.front[0], fy2 = y0 + v.front[1], bx = x0 + v.back[0];
  halo(HL.head, fx + L.dir * 4, fy2, 0.8); halo(HL.tail, bx, fy2, 0.6);
  if (v.light) halo(HL.taxi, x0 + v.light[0], y0 + v.light[1], 0.8);
  if (v.bus) halo(HL.busWin, x0 + (v.w >> 1), y0 + v.side + ((v.h - v.side) >> 1) - 1, 0.7);
  if (v.beacon) {                                     // the sweeper's orange beacon turning, its brushes and spray
    const on = frac(t * 1.6) < 0.5;
    px(ctx, x0 + v.beacon[0], y0 + v.beacon[1], on ? '#ffb030' : '#8a4a10');
    halo(HL.beacon, x0 + v.beacon[0], y0 + v.beacon[1], on ? 1 : 0.25);
    const bxr = L.dir > 0 ? x0 + v.w - 2 : x0 + 1, sp = step(t, 4800);
    for (let k = 0; k < 6; k++) {
      const hv = hash(k, sp, 61);
      ctx.globalAlpha = 0.6; px(ctx, bxr + L.dir * Math.round(1 + hv * 4), L.y - Math.round(hash(k, sp, 62) * 3), '#cfe4f0');
    }
    ctx.globalAlpha = 1;
    const r = step(t, 2400) % 4;
    px(ctx, bxr + [0, 1, 0, -1][r], L.y + [-1, 0, 1, 0][r] - 1, '#8a8a90');
  }
  if (v.blues) {                                      // the ambulance's blue lights, alternating
    const ph = Math.floor(t * 8) & 1;
    v.blues.forEach(([bx2, by], k) => {
      const on = (k & 1) === ph;
      px(ctx, x0 + bx2, y0 + by, on ? '#8ab4ff' : '#2a3a8a'); px(ctx, x0 + bx2 + 1, y0 + by, on ? '#e0ecff' : '#2a3a8a');
      if (on) halo(HL.blue, x0 + bx2, y0 + by, 1);
    });
  }
}
function drawLane(t, li) {
  const L = LANES[li], V = VEH[li];
  for (let k = 0; k < L.slots; k++) {
    const ph = k / L.slots + hash(li, k, 3) * 0.12;
    const q = t * L.n / LOOP + ph, f = Math.floor(q), cyc = f % L.n, u = q - f;
    const t0 = (f - ph) * LOOP / L.n, t1 = t0 + LOOP / L.n;
    if (SPECIALS.some(s => s.lane === li && t0 < s.b + 8 && t1 > s.a - 8)) continue;
    const hv = hash(li * 7 + k, cyc, L.seed + 20);
    if (hv < 0.26) continue;
    const v = hv > 0.955 && V.bus ? V.bus : hv < 0.52 ? V.taxi : V.cars[Math.floor(hash(li, cyc, k + 40) * V.cars.length)];
    drawVehicle(v, L, laneX(L, u), t, V.beams);
  }
  for (const s of SPECIALS) {
    if (s.lane !== li || t < s.a || t >= s.b) continue;
    drawVehicle(V[s.kind], L, laneX(L, (t - s.a) / (s.b - s.a)), t, V.beams);
  }
}
function drawWetTrail(t) {                            // the sweeper leaves its lane wet for a while
  const s = SPECIALS[2], L = LANES[s.lane];
  if (t < s.a || t > s.b + 40) return;
  for (let xs = 0; xs < W; xs += 4) {
    const tp = s.a + (L.dir > 0 ? xs + 80 : W + 80 - xs) / PATH * (s.b - s.a);
    if (t < tp + 0.5) continue;
    const a = 0.45 * (1 - smooth(tp, tp + 34, t));
    if (a <= 0.01) continue;
    ctx.globalAlpha = a; rect(ctx, xs, L.y - 3, 4, 3, '#07060c');
    for (const f of FAROLAS) if (Math.abs(xs + 2 - f.x) < 3) { ctx.globalAlpha = a * 1.4; rect(ctx, xs, L.y - 2, 4, 1, '#ffc878'); }
    if (hash(xs, 1, 63) < 0.2) { ctx.globalAlpha = a; px(ctx, xs + 1, L.y - 1, '#a88a78'); }
  }
  ctx.globalAlpha = 1;
}
function drawSirenWash(t) {                           // the ambulance's blue flicker on the Metrópolis and the plaza
  const s = SPECIALS[3];
  if (t < s.a - 1.5 || t >= s.b + 1) return;
  const x = laneX(LANES[s.lane], clamp((t - s.a) / (s.b - s.a), 0, 1)), on = (Math.floor(t * 8) & 1) === 0;
  const a = env(t, s.a - 1.5, s.b + 1, 0.8) * (on ? 0.8 : 0.35);
  halo(HL.blueWash, clamp(x, 60, 420), 176, a);
}

/* ================= The billboard: a rooftop neon for a churros bar, on a steel frame ================= */
// BB is its face (exported as `billboard`): promo captures paint the pitch there, everything else shows the neon.
const BB = { x: 244, y: 80, w: 56, h: 22 };
const BB_DECK = BB.y + BB.h + 2;                      // catwalk row under the frame
const BB_LEGS = [247, 263, 280, 295];
const roofAt = x => {                                 // top row of the Gran Vía roof under column x
  const z = sideZ(GV_S, x + 0.5), b = GV_ROW.find(r => z >= r.z0 && z <= r.z1);
  return b ? Math.round(fy(z, b.f + (b.roof || 0))) : Math.round(fy(z, 1));
};
const NEON_W = [2, 9, 16, 23, 30, 37, 44].map(k => k + 2);     // x of each CHURROS letter inside the face (5 wide, gap 2)
function neonStrokes(ch) {                            // a FONT3 glyph drawn as thin neon tubing at 2x: 5 x 9
  const g = FONT3[ch], on = (i, j) => j >= 0 && j < 5 && i >= 0 && i < 3 && g[j][i] === '1', pts = new Map();
  const add = (a, b) => pts.set(a * 16 + b, [a, b]);
  for (let j = 0; j < 5; j++) for (let i = 0; i < 3; i++) if (on(i, j)) {
    add(2 * i, 2 * j);
    if (on(i + 1, j)) add(2 * i + 1, 2 * j);
    if (on(i, j + 1)) add(2 * i, 2 * j + 1);
    if (on(i + 1, j + 1) && !on(i + 1, j) && !on(i, j + 1)) add(2 * i + 1, 2 * j + 1);
    if (on(i - 1, j + 1) && !on(i - 1, j) && !on(i, j + 1)) add(2 * i - 1, 2 * j + 1);
  }
  return [...pts.values()];
}
function neonSprite(pts, w, h, tube, glow) {          // tube pixels plus a 1 px glow on the panel around them (w, h incl. margin)
  const [c, x] = makeCanvas(w, h), set = new Set(pts.map(([a, b]) => (a + 1) * 64 + b + 1));
  if (glow) for (const [a, b] of pts) for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const k = (a + 1 + dx) * 64 + b + 1 + dy;
    if (!set.has(k)) px(x, a + 1 + dx, b + 1 + dy, glow);
  }
  for (const [a, b] of pts) px(x, a + 1, b + 1, tube);
  return c;
}
function textPts(s) {                                 // FONT3 pixels of a line of text (1x)
  const pts = [];
  let cx = 0;
  for (const ch of s) { FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') pts.push([cx + i, j]); }); cx += 4; }
  return pts;
}
const BORDER_PTS = [];
for (let i = 1; i < BB.w - 1; i++) { BORDER_PTS.push([i, 0]); BORDER_PTS.push([i, BB.h - 1]); }
for (let j = 1; j < BB.h - 1; j++) { BORDER_PTS.push([0, j]); BORDER_PTS.push([BB.w - 1, j]); }
function renderNeon() {
  const panel = (() => {
    const [c, x] = makeCanvas(BB.w, BB.h);
    rect(x, 0, 0, BB.w, BB.h, '#0c0816');
    for (let i = 3; i < BB.w; i += 4) rect(x, i, 1, 1, BB.h - 2, '#130e20');
    for (let j = 3; j < BB.h; j += 5) rect(x, 1, j, BB.w - 2, 1, '#110c1c');
    return c;
  })();
  const letters = [...'CHURROS'].map(ch => {
    const p = neonStrokes(ch);
    return [neonSprite(p, 7, 11, '#3a1c2c', null), neonSprite(p, 7, 11, '#ffd6e0', '#7a1c3c')];
  });
  const l2 = textPts('Y CHOCOLATE');
  const line2 = [neonSprite(l2, 45, 7, '#3a2418', null), neonSprite(l2, 45, 7, '#ffe2a0', '#5a3014')];
  const border = [neonSprite(BORDER_PTS, BB.w + 2, BB.h + 2, '#3a1a2a', null), neonSprite(BORDER_PTS, BB.w + 2, BB.h + 2, '#ff6a88', '#4a1428')];
  return { panel, letters, line2, border };
}
function renderBillboardFrame() {                     // steel frame, catwalk and legs down to the roofs
  const [c, x] = makeCanvas(W, H);
  for (const lx of BB_LEGS) {
    const r = roofAt(lx);
    for (let y = BB_DECK + 1; y < r; y++) { px(x, lx, y, '#1c1826'); px(x, lx + 2, y, '#141220'); if (y & 1) px(x, lx + 1, y, '#18152a'); }
    rect(x, lx - 1, r - 1, 5, 1, '#2a2436');
  }
  for (let k = 0; k < BB_LEGS.length - 1; k++) {      // cross bracing between the legs
    const a = BB_LEGS[k] + 2, b = BB_LEGS[k + 1], r = Math.min(roofAt(a), roofAt(b)) - 2;
    for (let i = a; i <= b; i++) {
      const u = (i - a) / (b - a), y1 = Math.round(lerp(BB_DECK + 2, r, u)), y2 = Math.round(lerp(r, BB_DECK + 2, u));
      px(x, i, y1, '#18152a'); px(x, i, y2, '#18152a');
    }
  }
  rect(x, BB.x - 4, BB_DECK, BB.w + 8, 1, '#2e2a40');                                // catwalk
  for (let i = BB.x - 4; i < BB.x + BB.w + 4; i += 3) { px(x, i, BB_DECK, '#4a4462'); px(x, i, BB_DECK - 1, '#1e1a2c'); }
  rect(x, BB.x - 2, BB.y - 2, BB.w + 4, BB.h + 3, '#15121f');                        // frame (the face covers the inside)
  rect(x, BB.x - 2, BB.y - 2, BB.w + 4, 1, '#3e3a58');                               // moonlit top edge
  rect(x, BB.x + BB.w + 1, BB.y - 1, 1, BB.h + 1, '#221e32');
  for (const bx of [BB.x - 2, BB.x + BB.w + 1]) px(x, bx, BB.y - 3, '#2a2638');       // corner lugs
  return c;
}
const BB_BUZZ = [[23.4, 24.6], [88.1, 89.9], [211.3, 212.2]];   // the second R acting up
const BB_CYCLE = 146;                                 // a power cycle: all off, then letter by letter back on
function neonState(t) {
  const L = [1, 1, 1, 1, 1, 1, 1];
  let line2 = 1, border = 1;
  const u = t - BB_CYCLE;
  if (u >= 0 && u < 3.6) {
    for (let k = 0; k < 7; k++) {
      const on = u - (0.8 + k * 0.22);
      L[k] = on < 0 ? 0 : on < 0.16 ? (hash(k, Math.floor(u * 30), 4) < 0.55 ? 1 : 0.3) : 1;
    }
    border = u > 2.5 ? (u < 2.7 ? 0.4 : 1) : 0;
    line2 = u > 3.0 ? (u < 3.3 ? (Math.floor(u * 20) & 1) : 1) : 0;
  }
  if (BB_BUZZ.some(([a, b]) => t >= a && t < b)) { const hv = hash(9, Math.floor(t * 26), 1); L[4] = hv > 0.5 ? 1 : hv > 0.25 ? 0.4 : 0; }
  const hum = 0.95 + 0.05 * wave(t, 2400);
  return { L, line2, border, hum, level: (L.reduce((a, b) => a + b, 0) / 7 * 0.7 + border * 0.2 + line2 * 0.1) * hum };
}
function drawBillboard(t, pitch) {
  const s = neonState(t);
  ctx.drawImage(BBF_C, 0, 0);
  halo(HL.neonRoof, BB.x + (BB.w >> 1), BB_DECK + 6, 0.8 * s.level);                // pink light on the roofs below
  if (!PITCH.covers(t, pitch)) {
    const N = NEON;
    ctx.drawImage(N.panel, BB.x, BB.y);
    const blit = (spr, v, x, y) => {
      ctx.drawImage(spr[0], x, y);
      if (v > 0) { ctx.globalAlpha = v * s.hum; ctx.drawImage(spr[1], x, y); ctx.globalAlpha = 1; }
    };
    ctx.drawImage(N.border[0], 1, 1, BB.w, BB.h, BB.x, BB.y, BB.w, BB.h);
    if (s.border > 0) { ctx.globalAlpha = s.border * s.hum; ctx.drawImage(N.border[1], 1, 1, BB.w, BB.h, BB.x, BB.y, BB.w, BB.h); ctx.globalAlpha = 1; }
    for (let k = 0; k < 7; k++) blit(N.letters[k], s.L[k], BB.x + NEON_W[k] - 1, BB.y + 1);
    blit(N.line2, s.line2, BB.x + 5, BB.y + 13);
    if (s.line2 > 0) {                                // two little stars that wink in turn
      const f = frac(t / 1.2) < 0.5;
      for (const [sx, on] of [[BB.x + 2, f], [BB.x + BB.w - 3, !f]]) {
        px(ctx, sx, BB.y + 16, on ? '#fff0c0' : '#6a4020');
        if (on) { px(ctx, sx - 1, BB.y + 16, '#ffb050'); px(ctx, sx + 1, BB.y + 16, '#ffb050'); px(ctx, sx, BB.y + 15, '#ffb050'); px(ctx, sx, BB.y + 17, '#ffb050'); }
      }
    }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  halo(HL.neon, BB.x + (BB.w >> 1), BB.y + 7, s.level);
  halo(HL.neonAmber, BB.x + (BB.w >> 1), BB.y + 16, s.line2 * 0.8);
}

/* ================= Our balcony ================= */
const roomLight = (x, y) => clamp(1 - Math.hypot((x - 446) / 120, (y - 238) / 70), 0, 1);   // the warm spill from the door
const POSTS = [40, 124, 208, 292, 376];
const LANTERN = { x: 452, y: 34 };                    // the wall lantern by the door
const GLASS = { x: 350, y: 194 };
// the glass without its ice and the straw's tip, which move (drawGlass)
const GLASS_ROWS = ['......', '.y....', 'wyy.sw', 'wR.Rsw', 'wRRRRw', 'wRRRRw', 'wRRRRw', 'wrRRRw', 'wrRRRw', 'wrrRRw', 'wrrrRw', '.wwww.'];
const GLASS_C = { w: '#d0dcea', y: '#ffe066', q: '#f0b0c0', R: '#b82038', r: '#781024', s: '#2a2830' };
const CHAIR = { x: 372, w: 22 };
const CURTAIN = { x: JAMB + 4, y0: 75, y1: 244, w: 9 };   // a net curtain on a rod under the persiana, drawn to the left
const CAT = { x: 110, y: 252 };                       // the cat's feet
const CAT_ROWS = ['..#.....#....', '..##...##....', '..#######....', '.#########...', '.#########...', '..#######....',
  '...#####.....', '..#######....', '.#########...', '.##########..', '###########..', '###########..',
  '############.', '############.', '.##########..', '..########...'];
const GERANIUMS = [                                   // flower clusters: x, y, size, sway period
  { x: 6, y: 208, r: 3, k: 17 }, { x: 16, y: 202, r: 3, k: 13 }, { x: 27, y: 206, r: 3, k: 19 }, { x: 35, y: 214, r: 2, k: 15 },
  { x: 11, y: 219, r: 2, k: 21 }, { x: 23, y: 216, r: 2, k: 11 }, { x: 43, y: 223, r: 2, k: 16 }, { x: 53, y: 226, r: 2, k: 14 },
  { x: 59, y: 233, r: 2, k: 18 }, { x: 66, y: 190, r: 2, k: 12 }, { x: 75, y: 187, r: 3, k: 20 }, { x: 85, y: 191, r: 2, k: 15 },
];
const LEAVES = [];                                    // geranium leaves: lobed discs
{
  const r = rng(808);
  for (let k = 0; k < 26; k++) LEAVES.push({ x: 2 + r() * 36, y: 206 + r() * 30, r: 2 + r() * 2 });
  for (let k = 0; k < 12; k++) LEAVES.push({ x: 36 + r() * 26, y: 224 + r() * 16, r: 1.6 + r() * 1.6 });
  for (let k = 0; k < 10; k++) LEAVES.push({ x: 61 + r() * 30, y: 192 + r() * 10, r: 1.5 + r() * 1.5 });
  for (let k = 0; k < 6; k++) LEAVES.push({ x: 64 + r() * 26, y: 207 + r() * 8, r: 1.3 + r() * 1.2 });   // trailing over the rail
}
const STRING = [];                                    // string lights under the handrail: bulbs on sagging swags
const STRING_X0 = 96, STRING_SPAN = 27;
const stringY = x => { const u = ((x - STRING_X0) % STRING_SPAN) / STRING_SPAN; return 209 + Math.round(5 * 4 * u * (1 - u)); };
{
  for (let i = STRING_X0; i <= 420; i += 6) {         // s: how far down its swag the bulb hangs (1 mid-swag), for the sway
    const u = ((i - STRING_X0) % STRING_SPAN) / STRING_SPAN;
    STRING.push({ id: STRING.length, x: i, y: stringY(i), k: 17 + (STRING.length * 7) % 40, ph: hash(i, 9),
      s: 4 * u * (1 - u), swag: Math.floor((i - STRING_X0) / STRING_SPAN) });
  }
}
// the folding chair, its back to us, an open red fan resting on it (baked into the balcony, and again on its own canvas
// to cover the cat when it walks under the seat)
function paintChair(x) {
  const { x: cx, w } = CHAIR, lit = cx + w - 1;
  for (const k of [1, w - 3]) for (let y = 218; y < 262; y++) { px(x, cx + k, y, '#5a3620'); px(x, cx + k + 1, y, cx + k + 1 > lit - 4 ? '#c07a48' : '#9a6038'); }
  for (let k = 1; k < w - 1; k++) {
    px(x, cx + k, 218, '#d08a52'); px(x, cx + k, 219, '#a86a3e'); px(x, cx + k, 220, '#8a5432'); px(x, cx + k, 221, '#4a2c1a');
    px(x, cx + k, 227, '#b0703f'); px(x, cx + k, 228, '#5a3620');
  }
  for (let k = 0; k < w; k++) { px(x, cx + k, 236, '#c07a48'); px(x, cx + k, 237, '#7a4a2c'); px(x, cx + k, 238, '#3a2214'); }
  for (let j = 0; j <= 22; j++) {
    const u = j / 22;
    px(x, cx + 3 + Math.round(u * (w - 7)), 239 + j, '#8a5432'); px(x, cx + w - 4 - Math.round(u * (w - 7)), 239 + j, '#5a3620');
  }
  rect(x, cx + 2, 261, 3, 1, '#2a1810'); rect(x, cx + w - 5, 261, 3, 1, '#2a1810');
  const fx = cx + 11, fy2 = 218;
  for (let dy = -6; dy <= 0; dy++) for (let dx = -6; dx <= 6; dx++) {
    const d = Math.hypot(dx, dy);
    if (d > 6.2 || d < 1) continue;
    const sec = Math.floor((Math.atan2(-dy, dx) / Math.PI) * 9);
    px(x, fx + dx, fy2 + dy, d > 5.3 ? '#ff7a7a' : (sec & 1) ? '#c81e30' : '#e8323e');
  }
  px(x, fx, fy2, '#3a2010'); px(x, fx, fy2 + 1, '#3a2010');
}
function renderBalcony() {
  const P = painter(W, H);
  // floor tiles, lit by the door: hydraulic tiles, beige with a blue-grey diamond, and terracotta
  const ROWS = [FLOOR, 249, 254, 260, 267, H + 5];
  for (let y = FLOOR; y < H; y++) for (let i = 0; i < W; i++) {
    const L = roomLight(i, y), r = ROWS.findIndex((v, k) => y >= v && y < ROWS[k + 1]), rf = (y - ROWS[r]) / (ROWS[r + 1] - ROWS[r]);
    const X = 240 + (i - 240) * (FLOOR - HZ) / (y - HZ), col = Math.floor(X / 12), cf = X / 12 - col;
    const beige = ((r + col) & 1) === 0, v = 0.12 + L * 0.88;
    let c;
    if (y === ROWS[r] || cf < 0.08) c = v > 0.55 ? '#4a2a1c' : v > 0.3 ? '#2a1812' : '#120a0e';
    else if (beige && Math.abs(cf - 0.54) + Math.abs(rf - 0.5) * 0.7 < 0.2) c = v > 0.55 ? '#4a6a78' : v > 0.3 ? '#2a3a46' : '#161a24';
    else c = beige ? (v > 0.6 ? '#a8845e' : v > 0.4 ? '#6a5040' : v > 0.22 ? '#3a2c28' : '#221a1c') : (v > 0.6 ? '#8a3e26' : v > 0.4 ? '#5a2a1e' : v > 0.22 ? '#321a18' : '#1c1014');
    P.put(i, y, c);
  }
  // our wall with the door: stucco above the lintel, the jamb, the room inside
  for (let y = 0; y < FLOOR; y++) for (let i = SHUT; i < W; i++) {
    let c;
    if (y < 62 || i < JAMB) c = pick(['#1c1624', '#2a2030', '#3e2e38', '#5a4038'], 0.2 + roomLight(i, y + 60) * 0.4 + (i < SHUT + 2 ? 0.25 : 0) + clamp(1 - Math.hypot((i - LANTERN.x) / 26, (y - LANTERN.y) / 30), 0, 1) * 0.7, i, y);
    else if (y < 66) c = y === 62 ? '#8a6a58' : '#3a2a2a';                                  // lintel
    else if (i < JAMB + 4) c = i === JAMB + 3 ? '#e0a870' : i === JAMB ? '#4a3a3a' : '#9a7058';   // stone jamb, lit on its inside
    else {                                            // the room: a warm wall, a bookshelf, a framed picture, a floor lamp
      const lamp = clamp(1 - Math.hypot((i - 466) / 40, (y - 150) / 90), 0, 1);
      c = pick(['#4a2618', '#6e3a22', '#94562e', '#bc7a40', '#e0a45e'], 0.15 + lamp * 0.8, i, y);
      if (y > 228) c = pick(['#3a2016', '#6a3a22', '#9a5a30'], 0.3 + lamp * 0.6, i, y);    // floorboards
      if (i >= 441 && i <= 452 && y >= 150 && y <= 244) {
        c = (y - 150) % 16 === 0 ? '#3a2218' : ['#7a2a2a', '#2a4a6a', '#c8a050', '#3a6a3a', '#8a5a8a', '#b86a3a'][Math.floor(hash(i, Math.floor((y - 150) / 16), 81) * 6)];
        if (i === 441 || i === 452) c = '#2a1810';
      }
      if (i >= 456 && i <= 475 && y >= 92 && y <= 116) {
        const fr = i === 456 || i === 475 || y === 92 || y === 116;
        c = fr ? '#c8983e' : Math.hypot(i - 466, y - 102) < 3.2 ? '#d8382a' : y < 106 ? '#34406a' : y < 108 ? '#e0a040' : '#a86a30';
      }
    }
    P.put(i, y, c);
  }
  const c = P.done(), x = P.x;
  // the wall lantern: an iron bracket and a glass box
  { const { x: lx, y: ly } = LANTERN;
    rect(x, lx, ly - 7, 9, 1, '#1a1418'); rect(x, lx + 8, ly - 9, 1, 5, '#1a1418'); px(x, lx + 1, ly - 8, '#1a1418');
    rect(x, lx - 3, ly - 5, 7, 1, '#1a1418'); rect(x, lx - 2, ly - 6, 5, 1, '#1a1418'); px(x, lx, ly - 7, '#1a1418');
    rect(x, lx - 2, ly - 4, 5, 7, '#ffd890'); rect(x, lx - 1, ly - 3, 3, 5, '#fff4d0');
    for (const k of [-2, 0, 2]) rect(x, lx + k, ly - 4, 1, 7, '#2a2024');
    rect(x, lx - 3, ly + 3, 7, 1, '#1a1418'); rect(x, lx - 1, ly + 4, 3, 1, '#1a1418'); px(x, lx, ly + 5, '#1a1418'); }
  // the rolled-up persiana and its strap, the floor lamp
  rect(x, JAMB + 4, 66, W - JAMB - 4, 7, '#5a3a24');
  for (let y = 67; y < 73; y += 2) rect(x, JAMB + 4, y, W - JAMB - 4, 1, '#7a5230');
  rect(x, JAMB + 4, 73, W - JAMB - 4, 1, '#2a1a12');
  rect(x, JAMB + 4, CURTAIN.y0 - 1, W - JAMB - 4, 1, '#3a2418');                          // the curtain rod
  rect(x, 462, 140, 10, 1, '#fff0c0'); rect(x, 461, 141, 12, 4, '#ffe0a0'); rect(x, 460, 145, 14, 2, '#ffd08a');
  rect(x, 466, 147, 1, 97, '#3a2818'); rect(x, 463, 243, 7, 1, '#2a1a10');
  // the shutter, folded open against the wall: green louvres, warm on the side facing the door
  for (let y = 64; y < FLOOR; y++) for (let i = SHUT; i < JAMB; i++) {
    const fr = i === SHUT || i === JAMB - 1 || y === 64 || y === FLOOR - 1 || y === 150;
    const L = (i - SHUT) / (JAMB - SHUT) * 0.5 + roomLight(i, y) * 0.5;
    px(x, i, y, fr ? pick(['#10221a', '#2a4a36'], L, i, y) : (y & 1) ? pick(['#16301f', '#2e5a3e', '#4e7a52'], L, i, y) : pick(['#0c1a12', '#1a3424'], L, i, y));
  }
  for (const hy of [80, 226]) rect(x, JAMB - 2, hy, 3, 2, '#1a1418');                       // hinges
  // the wrought-iron railing
  for (let i = 0; i < SHUT; i++) {
    const L = roomLight(i, 215);
    px(x, i, RAIL - 1, '#1a141e');
    px(x, i, RAIL, pick(['#6a6078', '#8a7a88', '#d0a070'], 0.3 + L, i, RAIL));
    px(x, i, RAIL + 1, '#2a2230'); px(x, i, RAIL + 2, '#16121c');
    px(x, i, 217, '#1a141e'); px(x, i, 235, '#1a141e'); px(x, i, 243, '#2a2230'); px(x, i, 244, '#120e16');
    const sy = 239 + Math.round(2 * Math.sin(i * Math.PI / 5));
    px(x, i, sy, L > 0.3 ? '#5a4038' : '#1a141e');
    const rc = (i % 10) - 5;                          // rings in the top band
    for (let y = 209; y < 217; y++) if (Math.abs(Math.hypot(rc + 0.5, y - 212.5) - 3) < 0.55) px(x, i, y, L > 0.3 ? '#5a4038' : '#1a141e');
    if (i % 5 === 2) for (let y = RAIL + 3; y < 244; y++) px(x, i, y, L > 0.55 ? '#7a5238' : L > 0.35 ? '#3e2c2a' : '#120e16');
  }
  for (const p of POSTS) {
    rect(x, p, RAIL - 1, 2, 245 - RAIL + 1, '#15111a'); rect(x, p, RAIL + 3, 1, 240 - RAIL, '#2e2836');
    rect(x, p - 1, RAIL - 3, 4, 2, '#1e1826'); px(x, p, RAIL - 4, '#1e1826'); px(x, p, RAIL - 3, '#5a5068');
  }
  paintChair(x);
  // the glass of tinto de verano on the rail
  GLASS_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (GLASS_C[row[i]]) px(x, GLASS.x + i, GLASS.y + j, GLASS_C[row[i]]); });
  // geranium pots
  const pot = (x0, x1, y0, y1) => {
    for (let y = y0; y < y1; y++) {
      const inset = Math.round((y - y0) * 0.18);
      for (let i = x0 + inset; i <= x1 - inset; i++) px(x, i, y, y < y0 + 2 ? (y === y0 ? '#c8744a' : '#8a4a30') : i > x1 - inset - 3 ? '#4a2418' : i < x0 + inset + 2 ? '#9a5234' : '#74391f');
    }
  };
  pot(4, 30, 234, 263); pot(38, 58, 240, 263);
  rect(x, 60, 200, 32, 6, '#7a3e24'); rect(x, 60, 200, 32, 1, '#b8683e'); rect(x, 60, 205, 32, 1, '#4a2418');   // planter on the rail
  for (const l of LEAVES) for (let dy = -3; dy <= 3; dy++) for (let dx = -3; dx <= 3; dx++) {
    const d = Math.hypot(dx, dy) + 0.35 * Math.sin(Math.atan2(dy, dx) * 5);
    if (d > l.r) continue;
    px(x, Math.round(l.x + dx), Math.round(l.y + dy), d > l.r - 0.8 ? (dx + dy < 0 ? '#5a9a4a' : '#12281a') : dx + dy < -1 ? '#347034' : dx === 0 && dy > 0 ? '#1a3a20' : '#265a2c');
  }
  return c;
}
/* The balcony's life, in loop seconds:
     all loop      moths round the wall lantern and along the string lights; the cat's tail flicks, its ears twitch
     12, 42 … 222  a gust off the plaza: the geraniums lean, the string lights swing out, the net curtain billows and a
                   petal comes off onto the tiles; the next two gusts nudge it along, the third blows it away
     18-32         the moth at the near end of the string comes down to the cat, which follows it and bats at it (25.2,
                   28.6); it flies back up on the second swipe
     41, 103, 157, 219   the ice settles in the glass and turns the straw
     70-76, 178-185      someone crosses the room: a shadow on the lit wall
     80, 106, 224  the cat looks round at us
     108-212       the cat's round: up and a stretch, over to sniff gust 3's petal by the small pot, along the tiles under
                   the chair to the lit doorway, a wash there (it looks into the room as the shadow passes), back home */
const GUST_T = 30, GUST_AT = 12, GUSTS = LOOP / GUST_T;
const gustNo = t => ((Math.floor((t - GUST_AT) / GUST_T) % GUSTS) + GUSTS) % GUSTS;
function gust(t) {                                    // 0..1: it builds over 2.5 s and dies away 4 to 10 s after it starts
  const g = (((t - GUST_AT) % GUST_T) + GUST_T) % GUST_T;
  return (0.55 + 0.45 * hash(gustNo(t), 71)) * smooth(0, 2.5, g) * (1 - smooth(4, 10, g));
}
// gust n's petal: off a cluster in the pots as the gust peaks, down to the tiles right of the pots (the air blows that
// way); gust 3's lands by the small pot, where the cat comes to sniff it
function petalAt(n, t) {
  const age = (((t - GUST_AT - n * GUST_T) % LOOP) + LOOP) % LOOP, life = 3 * GUST_T + 7;
  if (age < 1.5 || age >= life) return null;
  const g = GERANIUMS[Math.floor(hash(n, 72) * 9)];
  const lx = n === 3 ? 62 : 61 + Math.floor(hash(n, 73) * 32), ly = n === 3 ? 251 : 247 + Math.floor(hash(n, 74) * 17);
  if (age < 5.5) {
    const u = (age - 1.5) / 4;
    return { x: lerp(g.x, lx, u) + 2 * Math.sin(u * 8 + n), y: lerp(g.y, ly, u), fall: true, a: 1 };
  }
  const nudge = k => smooth(k * GUST_T + 2, k * GUST_T + 5, age) * (2 + Math.floor(hash(n, 75 + k) * 4));
  return { x: lx + nudge(1) + nudge(2) + 28 * smooth(3 * GUST_T + 1.5, life, age), y: ly, fall: false,
    a: 1 - smooth(3 * GUST_T + 3, life, age) };
}
function drawPetals(t, near) {                        // near: the ones in front of the cat's row
  for (let n = 0; n < GUSTS; n++) {
    const p = petalAt(n, t);
    if (!p || (p.y > CAT.y) !== near) continue;
    const X = Math.round(p.x), Y = Math.round(p.y);
    ctx.globalAlpha = p.a;
    if (p.fall) { const flat = (Math.floor(t * 6) + n) & 1; rect(ctx, X, Y, flat ? 2 : 1, 1, flat ? '#ff4a5a' : '#c81e30'); }
    else { px(ctx, X, Y, '#a01828'); px(ctx, X + 1, Y, '#5a0e1c'); }
  }
  ctx.globalAlpha = 1;
}

// moths: two round the wall lantern, one working along the string lights over the street, and one at the near end of
// them that comes down to the cat. A route is [arrival, bulb x, or x and y]; the moth circles there until it flies the
// last 3 s to the next stop, the last stop back to the first
const LAMP_MOTHS = [{ id: 1, rx: 7, ry: 8, k: 61, ph: 0.1 }, { id: 2, rx: 5, ry: 5, k: -83, ph: 0.6 }];
const ROUTE_MOTHS = [
  { id: 3, k: 71, ph: 0.3, route: [[0, 318], [27, 344], [54, 372], [80, 398], [107, 416], [135, 388], [163, 358], [190, 332], [216, 306]] },
  { id: 4, k: -67, ph: 0.8, route: [[0, 126], [21, 117, 232], [32, 150], [72, 204], [112, 234], [152, 192], [192, 162], [220, 132]] },
];
const CAT_PAWS = [[25.2, 26], [28.6, 29.4]];          // the cat bats at the moth
function mothAt(m, cx, cy, rx, ry, t) {               // circling (cx, cy), fluttering off its path
  const an = TAU * (m.k * t / LOOP + m.ph), j = step(t, 1920);
  return [cx + Math.cos(an) * rx + (hash(m.id, j, 1) - 0.5) * 2.4,
    cy + Math.sin(an) * ry * 0.55 + Math.sin(2 * an) * ry * 0.45 + (hash(m.id, j, 2) - 0.5) * 2];
}
function routeMoth(m, t) {
  const R = m.route;
  let i = R.length - 1;
  while (R[i][0] > t) i--;
  const t1 = i + 1 < R.length ? R[i + 1][0] : LOOP, at = r => [r[1], r.length > 2 ? r[2] : stringY(r[1]) - 3];
  const [x0, y0] = at(R[i]), [x1, y1] = at(R[(i + 1) % R.length]), u = smooth(t1 - 3, t1, t);
  const [x, y] = mothAt(m, lerp(x0, x1, u), lerp(y0, y1, u) - 5 * Math.sin(Math.PI * u), 5, R[i].length > 2 ? 3 : 4, t);
  return [x, CAT_PAWS.some(([a, b]) => t >= a && t < b) && m.id === 4 ? y - 3 : y];   // a hop clear of the paw
}
function drawMoth(m, x, y, t) {                       // wings open (2 px) or shut, beating
  rect(ctx, Math.round(x), Math.round(y), hash(m.id, step(t, 2880), 3) < 0.5 ? 2 : 1, 1, '#f2e6d0');
}

const SETTLE = [41, 103, 157, 219];                   // the ice settles in the glass, turning the straw
const ICE = [[[1, 3], [3, 3], [2, 4], [3, 4], [3, 5]], [[1, 4], [3, 4], [2, 5], [3, 5], [2, 6]]];
function drawGlass(t) {
  const n = SETTLE.filter(s => t >= s).length, lean = [0, 1, 0, -1][n & 3], since = n ? t - SETTLE[n - 1] : LOOP;
  ctx.fillStyle = GLASS_C.q;
  for (const [i, j] of ICE[n & 1]) ctx.fillRect(GLASS.x + i, GLASS.y + j, 1, 1);
  ctx.fillStyle = GLASS_C.s;                          // the straw's tip, upright or leaning on the rim
  ctx.fillRect(GLASS.x + 4, GLASS.y + 1, 1, 1); ctx.fillRect(GLASS.x + 4 + lean, GLASS.y, 1, 1);
  if (since < 1.4) px(ctx, GLASS.x + 2, GLASS.y + 10 - Math.floor(since / 1.4 * 7), '#e86a80');   // a bubble rising
  // a glint sliding down the glass every 7.5 s; the drink's red glow
  halo(HL.glass, GLASS.x + 3, GLASS.y + 7, 0.6);
  const g = frac(t / 7.5);
  if (g < 0.12) { const gy2 = GLASS.y + 3 + Math.floor(g / 0.12 * 7); px(ctx, GLASS.x + 1, gy2, '#ffffff'); halo(HL.glint, GLASS.x + 1, gy2, 1 - g / 0.12); }
}

function renderCurtain() {                            // a sheer lit from the room: soft folds, gathered on its rings, a hem
  const { w, y0, y1 } = CURTAIN, cw = w + 7, h = y1 - y0 + 1, [c, x] = makeCanvas(cw, h);
  for (let i = 0; i < cw; i++) {                      // a fold every 4 px: lit crest, then the valley
    x.fillStyle = `rgba(255,242,226,${[0.5, 0.38, 0.22, 0.34][i % 4]})`; x.fillRect(i, 2, 1, h - 5);
    x.fillStyle = 'rgba(255,242,226,0.62)'; x.fillRect(i, 0, 1, 2); x.fillRect(i, h - 3, 1, 3);
  }
  x.fillStyle = 'rgba(120,70,40,0.35)'; x.fillRect(0, h - 1, cw, 1);
  return c;
}
function drawCurtain(t, G) {                          // its foot swings out into the doorway, and lifts in a gust
  const { x: cx, y0, y1, w } = CURTAIN, sway = (0.6 + G) * wave(t, 17, 0.3), lift = Math.round(4 * G * G), yb = y1 - lift;
  let ya = y0, key = -1, off = 0, wd = 0, hem = 0;
  for (let y = y0; y <= yb + 1; y++) {                // rows in runs that share an offset and width; the hem rides up
    const s = (y - y0) / (y1 - y0), o = Math.max(0, Math.round(s * s * (1.5 * sway + 6 * G))), d = w + Math.round(s * (5 + 3 * G));
    const hm = y > yb - 3 ? lift : 0, k = y > yb ? -2 : o * 4096 + d * 16 + hm;
    if (k === key) continue;
    if (key >= 0) {
      ctx.drawImage(CURT_C, 0, ya - y0 + hem, wd, y - ya, cx + off, ya, wd, y - ya);
      ctx.fillStyle = 'rgba(150,90,50,0.45)'; ctx.fillRect(cx + off + wd - 1, ya, 1, y - ya);   // the shadowed edge
    }
    ya = y; key = k; off = o; wd = d; hem = hm;
  }
}

function drawBalconyFx(t) {
  const G = gust(t);
  // geranium flower clusters, nodding in the warm air and leaning with the gusts
  for (const g of GERANIUMS) {
    const dx = Math.round(0.7 * wave(t, g.k, g.x / 50) + G * (1.3 + 0.7 * wave(t, 4 * g.k, g.y / 30))), dy = Math.round(0.5 * wave(t, g.k + 3, g.y / 40));
    for (let j = -g.r; j <= g.r; j++) for (let i = -g.r; i <= g.r; i++) {
      const d = Math.hypot(i, j);
      if (d > g.r + 0.2 || hash(g.x + i, g.y + j, 5) < 0.18) continue;
      px(ctx, g.x + i + dx, g.y + j + dy, d < 0.8 ? '#ff9a9a' : i + j < 0 ? '#ff4a5a' : j > 0 ? '#a81428' : '#e0283c');
    }
  }
  drawPetals(t, false);
  // string lights: the swags sway a little, and swing out and up in a gust
  for (const b of STRING) {
    const I = (0.6 + 0.4 * wave(t, b.k, b.ph)) * (0.8 + 0.2 * wave(t, 6, -b.x / 300));
    const x = b.x + Math.round(b.s * (0.6 * wave(t, 23, b.swag * 0.17) + 1.6 * G)), y = b.y - Math.round(b.s * 1.3 * G);
    halo(HL.string, x, y + 1, I * 0.8);
    ctx.globalAlpha = 0.55 + 0.45 * I; px(ctx, x, y + 1, (b.id % 3) ? '#fff0c8' : '#ffc464');
    ctx.globalAlpha = 1;
  }
  drawGlass(t);
  for (const m of ROUTE_MOTHS) { const [x, y] = routeMoth(m, t); drawMoth(m, x, y, t); }
  // someone crossing the room now and then: a soft shadow on the lit wall, behind the curtain
  const sh = [[70, 76], [178, 185]].find(([a, b]) => t >= a && t < b);
  if (sh) { const u = (t - sh[0]) / (sh[1] - sh[0]); ctx.globalAlpha = 0.35 * Math.sin(Math.PI * u); rect(ctx, Math.round(lerp(484, 436, u)), 100, 10, 128, '#2a120a'); ctx.globalAlpha = 1; }
  drawCurtain(t, G);
  const catX = drawCat(t);
  drawPetals(t, true);
  // it walks under the seat: the chair's seat and legs again over it (the string lights stay over the chair's back)
  if (Math.abs(catX - CHAIR.x - (CHAIR.w >> 1)) < 24) ctx.drawImage(CHAIR_C, CHAIR.x, 236, CHAIR.w, 26, CHAIR.x, 236, CHAIR.w, 26);
  halo(HL.room, 452, 200, 0.95 + 0.05 * wave(t, 5));
  halo(HL.lampShade, 467, 144, 1);
  halo(HL.lantern2, LANTERN.x, LANTERN.y, 0.9 + 0.1 * wave(t, 1300));
  for (const m of LAMP_MOTHS) { const [x, y] = mothAt(m, LANTERN.x, LANTERN.y, m.rx, m.ry, t); drawMoth(m, x, y, t); }
}

/* ================= The cat: its poses and its round of the balcony ================= */
// its other poses, drawn facing right ('#' fur, e an eye) and mirrored to face left: a walk (one body, four leg frames),
// standing, the stretch, sniffing at the floor, sitting side on and washing a paw; and the back view with a paw up
const CAT_TOP = ['...............#..#..', '...............####..', '..##............###..', '.##............##e##.', '.#............######.',
  '.##...........######.', '..##..#############..', '...##############....', '....##############...', '.....############....'];
const CAT_LEGS = [
  ['.....#.#######.#.....', '.....#.#.....#.#.....', '....#...#.....#......', '....#...#.....#......', '....##..##....##.....'],
  ['.....#.#######.#.....', '.....#.#.....#.#.....', '.....#.#.....#.#.....', '.....#.#.....#.#.....', '.....####....####....'],
  ['.....#.#######.#.....', '.....#.#.....#.#.....', '......#.....#...#....', '......#.....#...#....', '......##....##..##...'],
  ['......#######.#......', '......#.......#......', '......#.......#......', '......#.......#......', '......##......##.....'],
];
const CAT_POSES = {
  stretch: ['..##....................', '.#......................', '.#......................', '..#.####................',
    '...#######..............', '...##########...........', '...#############...#..#.', '....##############.####.',
    '....###############.###.', '....#.##..########.##e##', '....#.#.....##########.#', '....#.#.......########..',
    '....#.#........#######..', '....#.#........########.', '...##.##........########'],
  sniff: ['..##.................', '.##..................', '.#...................', '.##..................',
    '..##..#########......', '...##############....', '....###############..', '.....##############..',
    '......#######.#.##.#..#', '......#.......#..######', '......#.......#..##e###', '......#.......#...#####', '......##......##...###.'],
  sit: ['.........#..#..', '.........##.##.', '.........#####.', '........###e##.', '........#######', '........#####..',
    '........####...', '.......#####...', '......######...', '.....#######...', '....########...', '...#########...',
    '..##########...', '..##########...', '#.#######.##...', '#########.###..'],
  groom: ['...............', '.........#..#..', '.........##.##.', '.........#####.', '........######.', '........#######',
    '........#####.#', '.......#####.##', '......######.#.', '.....#######.#.', '....########.#.', '...#########...',
    '..##########...', '..##########...', '#.#######.#....', '#########.##...'],
};
const CAT_BAT = ['..#.....#..#.', '..##...##.##.', '..#######.##.', '.#########.#.', '.#########.#.', '..#######.#..',
  '...#####.##..', '..#######.#..', '.#########...', '.##########..', '###########..', '###########..',
  '############.', '############.', '.##########..', '..########...'];
function catSprite(rows, flip) {                      // rim-lit like the sitting cat; warm: edges the room's light can catch
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), [c, x] = makeCanvas(w, h), warm = [];
  const at = (i, j) => (i < 0 || i >= w || j < 0 ? '.' : rows[j][flip ? w - 1 - i : i] || '.');
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const ch = at(i, j);
    if (ch === '.') continue;
    const thin = at(i - 1, j) === '.' && at(i + 1, j) === '.';   // legs and the tail: only a glint of the rim
    px(x, i, j, ch === 'e' ? '#c8ff5a' : thin ? '#24202e' : at(i - 1, j) === '.' ? '#3e3a58' : at(i, j - 1) === '.' ? '#34304c' : '#0c0a10');
    if (ch !== 'e' && at(i + 1, j) === '.') warm.push([i, j, thin ? 0.45 : 1]);
    else if (ch !== 'e' && at(i, j - 1) === '.') warm.push([i, j, 0.5]);
  }
  return { c, w, h, warm };
}
function buildCat() {
  const both = rows => [catSprite(rows, false), catSprite(rows, true)];
  const S = { walk: CAT_LEGS.map(l => both([...CAT_TOP, ...l])) };
  S.stand = S.walk[3];
  for (const k in CAT_POSES) S[k] = both(CAT_POSES[k]);
  return S;
}
const HOME = CAT.x + 6, SNIFF_X = 74, DOOR_X = 455;   // centres: its spot, by the small pot, in the doorway's light
const ROUND = [                                       // [from, to, pose, facing, x, x at the end of a walk]
  [108, 109, 'stand', -1, HOME], [109, 112, 'stretch', -1, HOME], [112, 115.5, 'walk', -1, HOME, SNIFF_X],
  [115.5, 122, 'sniff', -1, SNIFF_X], [122, 123, 'stand', 1, SNIFF_X], [123, 153, 'walk', 1, SNIFF_X, DOOR_X],
  [153, 153.8, 'stand', -1, DOOR_X], [153.8, 184, 'sit', -1, DOOR_X], [184, 185, 'stand', -1, DOOR_X],
  [185, 211, 'walk', -1, DOOR_X, HOME], [211, 212, 'stand', -1, HOME],
];
const CAT_TURNS = [[80, 86.5], [106, 108], [224, 228.5]];   // the cat looks round at us
function drawCat(t) {                                 // returns the cat's x
  const r = ROUND.find(([a, b]) => t >= a && t < b);
  if (r) {
    const [a, b, pose, facing, x0, x1] = r, x = x1 === undefined ? x0 : lerp(x0, x1, (t - a) / (b - a));
    let s = pose === 'walk' ? CAT_SPR.walk[step(t, 1600) & 3] : CAT_SPR[pose], dir = facing;
    if (pose === 'sniff' && frac(t / 1.6) > 0.75) s = CAT_SPR.stand;               // lifts its head now and then
    if (pose === 'sit') {
      if (t >= 178.5 && t < 183.5) dir = 1;                                         // looks into the room
      else if ((t - 148) % 8 < 3 && (Math.floor(t * 2) & 1)) s = CAT_SPR.groom;       // a wash every 8 s
    }
    const sp = s[dir > 0 ? 0 : 1], X = Math.round(x - sp.w / 2), Y = CAT.y - sp.h, L = Math.min(1, roomLight(x, 246) * 1.1);
    ctx.drawImage(sp.c, X, Y);
    if (L > 0.04) {                                   // the room's light on its edges as it nears the door
      ctx.fillStyle = '#e8a060';
      for (const [i, j, k] of sp.warm) { ctx.globalAlpha = L * k; ctx.fillRect(X + i, Y + j, 1, 1); }
      ctx.globalAlpha = 1;
    }
    return x;
  }
  const { x: cx, y: cy } = CAT, top = cy - CAT_ROWS.length;
  const turn = CAT_TURNS.some(([a, b]) => t >= a && t < b), paw = CAT_PAWS.some(([a, b]) => t >= a && t < b);
  const twitch = !paw && frac(t / 7.5) < 0.03;        // an ear flick now and then (32 a loop)
  let hx = turn ? 1 : 0;
  if (t >= 18 && t < 33) hx = clamp(Math.round((routeMoth(ROUTE_MOTHS[1], t)[0] - HOME) / 5), -1, 1);   // it follows the moth
  const rows = paw ? CAT_BAT : CAT_ROWS;
  rows.forEach((row, j) => { for (let i = 0; i < row.length; i++) {
    if (row[i] !== '#') continue;
    let X = cx + i;
    if (j < 6 && !(paw && i > 9)) X += hx;
    if (j < 2 && twitch && i > 6) continue;
    const rim = (i === 0 || row[i - 1] !== '#') ? '#3e3a58' : (j === 0 || rows[j - 1][i] !== '#') ? '#34304c' : null;
    px(ctx, X, top + j, rim || '#0c0a10');
  } });
  if (twitch) px(ctx, cx + 9, top, '#34304c');
  if (turn) { px(ctx, cx + 9, top + 3, '#c8ff5a'); px(ctx, cx + 10, top + 4, '#2a2030'); px(ctx, cx + 8, top + 3, '#0c0a10'); }
  // the tail curls across the floor, its tip flicking
  const sw = wave(t, 30) + 0.3 * wave(t, 97, 0.2);
  for (let k = 0; k <= 10; k++) {
    const s = k / 10, tx = cx + 11 + 6 * s + 1.6 * sw * s * s, ty = cy - 1 - 6 * s * s * (0.75 + 0.25 * sw);
    rect(ctx, Math.round(tx), Math.round(ty), s < 0.5 ? 2 : 1, 1, s > 0.85 ? '#3e3a58' : '#0c0a10');
  }
  return HOME;
}

/* ================= Poplar fluff (pelusas) drifting through the warm air, three depths ================= */
const FLUFF_DEFS = [
  { count: 60, nMin: 3, nMax: 6, y0: 20, y1: 192, amp: 6, fall: 34 },     // far: tiny, slow, behind the Metrópolis
  { count: 40, nMin: 5, nMax: 8, y0: 40, y1: 240, amp: 10, fall: 46 },    // over the plaza
  { count: 8, nMin: 3, nMax: 5, y0: 70, y1: 250, amp: 9, fall: 44 },     // past the balcony: a few, drifting slowly
];
const FLUFF = FLUFF_DEFS.map((d, li) => {
  const r = rng(909 + li), out = [];
  for (let i = 0; i < d.count; i++) out.push({ id: i + li * 100, n: d.nMin + Math.floor(r() * (d.nMax - d.nMin + 1)), ph: r(),
    k1: 3 + Math.floor(r() * 6), p1: r(), k2: 11 + Math.floor(r() * 14), p2: r(), spr: Math.floor(r() * 3) });
  return out;
});
// the near tufts wander: a slow swing across (k3) and a lazier rise and fall (k4) on top of the drift, and turn to their
// next shape every 3 to 6 s (kt steps a loop, a multiple of the shapes so the loop's wrap is an ordinary turn)
for (const f of FLUFF[2]) Object.assign(f, { k3: 2 + Math.floor(hash(f.id, 31) * 4), p3: hash(f.id, 32),
  k4: 5 + Math.floor(hash(f.id, 33) * 5), p4: hash(f.id, 34), kt: 3 * (14 + Math.floor(hash(f.id, 35) * 13)) });
// near tufts: a seed with its cotton and a fibre or two trailing, no rim of light around them (that reads as snow);
// 1 fibre, 2 cotton, 3 seed
const PUFFS = [['0100', '0230', '0001'], ['0010', '1320', '0000'], ['0000', '0321', '1000']];
const TINTS = ['#e8dcc6', '#fff0c8', '#ffc8d8', '#c8ffd8', '#c8f0ff'];
const NEAR_TINTS = ['#c8b8a0', '#ffdca4', '#ffbcc4', '#c4f0cc', '#c0e8ff'];   // the near tufts: warmer, dim out of the light
function renderPuffs() {                              // [shape][tint] sprites with soft alpha
  return PUFFS.map(rows => NEAR_TINTS.map(col => {
    const [c, x] = makeCanvas(4, 3), [r, g, b] = hex(col);
    rows.forEach((row, j) => { for (let i = 0; i < 4; i++) {
      const v = +row[i];
      if (!v) continue;
      x.fillStyle = `rgba(${r},${g},${b},${[0, 0.3, 0.6, 0.9][v]})`; x.fillRect(i, j, 1, 1);
    } });
    return c;
  }));
}
function lightAt(x, y, lights) {                      // index into TINTS of the light the point is in (0 = none)
  for (const l of lights) {
    const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry;
    if (dx * dx + dy * dy < 1) return l.tint || 1;
  }
  return 0;
}
const NEAR_GLOW = [{ x: 258, y: 212, rx: 168, ry: 7 }, { x: LANTERN.x, y: LANTERN.y, rx: 18, ry: 18 }];   // string lights, wall lantern
function drawFluff(li, t, lights) {
  const d = FLUFF_DEFS[li];
  for (const f of FLUFF[li]) {
    const { cyc, u } = cycle(t, f.n, f.ph), near = li === 2;
    const x = -12 + u * (W + 24) + (li + 1) * 2 * wave(t, f.k2, f.p2) + (near ? 10 * wave(t, f.k3, f.p3) : 0);
    const y = lerp(d.y0, d.y1, hash(f.id, cyc, 1)) + (u - 0.5) * d.fall * (hash(f.id, cyc, 2) - 0.4) + d.amp * wave(t, f.k1, f.p1)
      + (near ? 7 * wave(t, f.k4, f.p4) : 0);
    if (x < -6 || x > W + 6) continue;
    const tint = lightAt(x, y, lights) || (near ? lightAt(x, y, NEAR_GLOW) : 0), X = Math.round(x), Y = Math.round(y);
    if (near) {                                       // faint out of the light, catching it warm where a lamp is near
      ctx.globalAlpha = tint ? 0.95 : 0.5;
      ctx.drawImage(PUFF_C[(f.spr + step(t, f.kt)) % PUFFS.length][tint], X - 1, Y - 1);
      continue;
    }
    ctx.fillStyle = TINTS[tint];
    if (li === 0) { ctx.globalAlpha = tint ? 0.85 : 0.5; ctx.fillRect(X, Y, 1, 1); continue; }
    ctx.globalAlpha = tint ? 0.95 : 0.75; ctx.fillRect(X, Y, 1, 1);
    ctx.globalAlpha = tint ? 0.55 : 0.4; ctx.fillRect(X + 1, Y, 1, 1); ctx.fillRect(X, Y + 1, 1, 1);
    ctx.globalAlpha = tint ? 0.3 : 0.2; ctx.fillRect(X + 1, Y + 1, 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Lights the fluff, rain and snow catch ================= */
const LIGHTS = [
  ...FAROLAS.map(f => ({ x: f.x, y: f.y - 27, rx: 17, ry: 14 })),
  ...LAMPS.filter(l => l.z < 2).map(l => ({ x: l.x, y: l.y, rx: 9 / l.z, ry: 9 / l.z })),
  { x: 151, y: 187, rx: 20, ry: 8 },                                  // the café
  { x: BLADES[0].x + 2, y: BLADES[0].y + 12, rx: 7, ry: 16, col: '#ff8a9a', tint: 2 },
  { x: BLADES[1].x + 2, y: BLADES[1].y + 9, rx: 6, ry: 12, col: '#9af0ff', tint: 4 },
  { x: MX, y: 140, rx: 24, ry: 46, col: '#ffe8c0' },                  // the floodlit rotunda
  { x: 452, y: 205, rx: 50, ry: 62 },                                 // the room's light through the door
  { x: PHARM.x, y: PHARM.y, rx: 8, ry: 8, col: '#8affb0', tint: 3 },
];
const LIGHTS_BB = [...LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 10, rx: 40, ry: 20, col: '#ff9ab0', tint: 2 }];   // + the neon
// a picked weather (site/js/engine/weather.js): Madrid's own falling thing is the poplar fluff, which only shows in the city's
// own weather; far drops land on the far pavements and the plaza, mid ones on the lanes below us, near ones fall past
// the balcony and out of the bottom of the frame
const WEATHER = weatherPlans(['fluff']);
const WX_LAND = [[150, 196], [198, 244], [H + 4, H + 4]];
// nothing falls in the room behind the balcony door (the near depth is drawn over it), and no tree reaches a balcony
// this high: the wind carries the leaves along the street, left of our wall
const WX_SHELTER = [[], [], [[JAMB, 62, W, FLOOR]]], WX_LEAVES = { wind: [0, 24, SHUT, 196] };
const WX = { land: WX_LAND, lights: LIGHTS, shelter: WX_SHELTER, leaves: WX_LEAVES };
const WX_BB = { land: WX_LAND, lights: LIGHTS_BB, shelter: WX_SHELTER, leaves: WX_LEAVES };

function drawLamps(t) {
  for (const f of FAROLAS) {
    halo(HL.farola, f.x, f.y - 27, 0.9 + 0.1 * wave(t, 1100 + f.x));
    for (const [dx, dy] of [[-6, -28], [6, -28], [0, -30]]) halo(HL.farolaCore, f.x + dx, f.y + dy, 1);
  }
  for (const l of LAMPS) halo(HL.lampS[l.z < 1.6 ? 0 : l.z < 3 ? 1 : 2], l.x, l.y, 0.9);
}
function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 8; d[o + 1] = 4; d[o + 2] = 16; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, WISP_C, FAR_C, CITY_C, MET_C, STREET_C, CAFE_C, FAROLA_C, VEH, BBF_C, NEON, PITCH, BAL_C, PUFF_C, VIG_C;
let CHAIR_C, CURT_C, CAT_SPR;
function prerender() {
  HL = {
    moon: makeHalo(60, '#d8c0a0', 0.16, 1.7), moonCore: makeHalo(22, '#fff2d8', 0.3, 1.5),
    red: makeHalo(4, '#ff3030', 0.6, 1), landing: makeHalo(8, '#fff6dc', 0.45, 1.4), strobe: makeHalo(5, '#ffffff', 0.6, 1),
    crown: makeHalo(12, '#ffc890', 0.22, 1.5), roofLamp: makeHalo(6, '#ffd08a', 0.4, 1.3),
    blade: BLADES.map(b => makeHalo(14, b.col, 0.3, 1.5)), marquee: makeHalo(16, '#ffc870', 0.3, 1.5),
    hostal: makeHalo(14, '#ff5a4a', 0.24, 1.5), pharm: makeHalo(9, '#3aff7a', 0.4, 1.4), belfry: makeHalo(7, '#ffb060', 0.4, 1.3),
    domeWash: makeHalo(46, '#ffcf8a', 0.16, 1.6), victory: makeHalo(18, '#ffd89a', 0.22, 1.5), lantern: makeHalo(8, '#ffe0a0', 0.4, 1.3),
    spark: makeHalo(3, '#ffffff', 0.7, 1), arcade: makeHalo(30, '#ffb060', 0.2, 1.6),
    farola: makeHalo(20, '#ffc070', 0.32, 1.5), farolaCore: makeHalo(5, '#fff0c8', 0.5, 1.2),
    lampS: [makeHalo(10, '#ffc070', 0.3, 1.5), makeHalo(6, '#ffc070', 0.3, 1.4), makeHalo(3, '#ffc070', 0.35, 1.2)],
    tl: ['#ff3a3a', '#ffb030', '#3aff9a'].map(c => makeHalo(5, c, 0.45, 1.2)),
    cafe: makeHalo(24, '#ffb868', 0.25, 1.6), bulb: makeHalo(3, '#ffd080', 0.5, 1),
    head: makeHalo(7, '#fff0c0', 0.3), tail: makeHalo(3, '#ff3030', 0.45, 1), taxi: makeHalo(3, '#3aff7a', 0.5, 1),
    busWin: makeHalo(22, '#ffd890', 0.14, 1.6), beacon: makeHalo(12, '#ffa020', 0.4, 1.4),
    blue: makeHalo(12, '#4a8aff', 0.5, 1.3), blueWash: makeHalo(60, '#3a6aff', 0.14, 1.5),
    neon: makeHalo(44, '#ff4d78', 0.2, 1.6), neonAmber: makeHalo(20, '#ffb050', 0.18, 1.5), neonRoof: makeHalo(30, '#ff5a88', 0.16, 1.5),
    string: makeHalo(5, '#ffd890', 0.4, 1.3), glass: makeHalo(6, '#ff5a6a', 0.25, 1.3), glint: makeHalo(3, '#ffffff', 0.7, 1),
    room: makeHalo(70, '#ffb060', 0.14, 1.6), lampShade: makeHalo(18, '#ffe0a0', 0.35, 1.4), lantern2: makeHalo(22, '#ffc070', 0.3, 1.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); WISP_C = WISPS.map(renderWisp); FAR_C = renderFar();
  WIN_TOG.length = 0; CITY_C = renderCity(); MET_C = renderMetropolis(); STREET_C = renderStreet();
  CAFE_C = renderCafe(); FAROLA_C = renderFarola(8, 33); VEH = buildVehicles();
  BBF_C = renderBillboardFrame(); NEON = renderNeon(); PITCH = makePitch(BB.w, BB.h, { accent: '#ff4d6d' });
  BAL_C = renderBalcony(); PUFF_C = renderPuffs(); VIG_C = renderVignette();
  { const [c, x] = makeCanvas(W, H); paintChair(x); CHAIR_C = c; }
  CURT_C = renderCurtain(); CAT_SPR = buildCat();
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the billboard; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, lights = billboard ? LIGHTS_BB : LIGHTS;
  ctx.drawImage(SKY_C, 0, 0);
  drawStars(t);
  drawShootingStars(t);
  halo(HL.moon, MOON.x, MOON.y); halo(HL.moonCore, MOON.x, MOON.y);
  ctx.drawImage(MOON_C, MOON.x - MOON.r, MOON.y - MOON.r);
  drawWisps(t);
  drawPlanes(t);
  ctx.drawImage(FAR_C, 0, 0);
  ctx.drawImage(STREET_C, 0, 0);
  ctx.drawImage(CITY_C, 0, 0);
  drawCityFx(t);
  drawBell(t);
  if (billboard) drawBillboard(t, pitch);
  drawStreams(t);
  drawStrollers(t);
  if (wx.fluff) drawFluff(0, t, lights);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  ctx.drawImage(MET_C, 0, 0);
  drawMetropolisFx(t);
  drawBats(t);
  drawSirenWash(t);
  drawWetTrail(t);
  ctx.drawImage(CAFE_C, 0, 0);
  drawCafe(t);
  drawWalkers(t);
  for (const f of FAROLAS) ctx.drawImage(FAROLA_C, f.x - 8, f.y - 33);
  drawLamps(t);
  drawTrafficLights(t);
  drawLane(t, 0);
  if (wx.fluff) drawFluff(1, t, lights);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawLane(t, 1);
  ctx.drawImage(BAL_C, 0, 0);
  drawBalconyFx(t);
  if (wx.fluff) drawFluff(2, t, lights);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'madrid',
  name: 'Madrid',
  tagline: 'Summer night on Gran Vía',
  timeZone: 'Europe/Madrid',
  accent: '#ff4d6d',
  focusX: MX,
  landmark: [MX - 18, MX + 18],                        // the Metrópolis rotunda and dome
  landmarkTop: VIC_Y,                                  // the row of the Victory statue's wings, which landscape crops keep
  billboard: BB,
  ambience: { traffic: 0.45, crowd: 0.22, wind: 0.12 },
  events: [...TOLLS.map(t => ({ t, type: 'bell', x: BELL.x })), { t: 112, type: 'siren', dur: 14, pan: [-1, 1] }],
  create(c) {
    ctx = c;
    prerender();
    return { render: renderFrame };
  },
};
